/* eslint-disable no-prototype-builtins */
/* eslint-disable guard-for-in */
/* eslint-disable no-loop-func */
/* eslint-disable max-depth */
/* eslint-disable no-use-before-define */
/* eslint-disable no-console */
import axios from 'axios';
import { formatLocale } from 'd3-format';
import { select } from 'd3-selection';
import { timeFormatDefaultLocale } from 'd3-time-format';
import EventEmitter from 'events';
import CallbackManager from './CallbackManager';
import { get, merge, forEachRight } from 'lodash';
import { deepClone } from './util/deepClone';
import { ChartLoader } from './util/ChartLoader';
import { buildCrosshairData } from './util/crosshair';
import pkg from '../package.json';
import { types as cursorTypes } from './enums/cursor';
import { types as eventTypes } from './enums/event';
import { markerType as priceMarkerTypes } from './enums/indicators/price';
import { domainAdjustmentCategories, renderDuplicateLabelsCategories } from './enums/modcharts';
import LocaleEnUs from './locale/en_US';
import Panel from './Panel';
import generateDefaultStyles from './style/default';
import { calcTradingDaysBetweenTradingDates } from './util/time';
import translate from './util/translate';
import createUid from './util/uid';
import zoomUtil from './util/zoom';
import { exportData, exportAllData } from './util/export';
import { zoomIdentity } from './util/zoom';

/** Initialize panels, params, state and overall root dom */
export default class Modcharts {
	/**
	 * @param {string} rootSelector Root DOM selector
	 * @param {object} params Custom chart params
	 * @param {object} parent Optional parent object reference
	 */
	constructor(rootSelector, params = {}, parent = null) {
		this.version = pkg.version;
		this.uid = createUid();
		this.eventEmitter = new EventEmitter();
		this.chartLoader = new ChartLoader(this);
		this.callbackManager = new CallbackManager();
		this.panels = [];
		/** Optional reference for parent object of chart (if one exists) */
		this.parent = parent;
		this.state = this.getChartState();
		// Generate the default styles, applying common style overrides if needed
		const defaultStyles = generateDefaultStyles(params.styleCommon);
		this.params = this.getDefaultParams(params);
		// Merge defaultStyles with specific style overrides provided in params
		this.styleMap = merge({}, defaultStyles, params.style);
		this.rootSelector = rootSelector;
		/** The status of if the chart is in a "Chart Not Available" state. 0 means the chart isn't available; 1 means it is. */
		this.status = 0;
		this.xref = {};
		this.metaData = {};
		this.timeService = {};
		this.data = {};
		/** XHR object used to retrieve chart data. Should contain an object when chart is requesting data; should be null when chart isn't requesting data. Many functions use this variable to determine how to proceed. */
		this.dataXHR = null;
		this.xhrCancelSource = null;
		this.dataPrimary = [];
		this.translate = translate; // expose translate method outside of chart
		this.setLocale(this.params.locale || LocaleEnUs);
		if (rootSelector) this.mount(rootSelector);
		this.exportData = exportData.bind(this);
		this.exportAllData = exportAllData.bind(this);

		return this;
	}

	/**
	 * Sets the DOM element that the chart will be rendered to
	 * @param {String} rootSelector - Selector string for root node
	 * @return {Boolean} Whether or not mount was successful
	 */
	mount(rootSelector) {
		this.rootModchart = select(rootSelector);

		// Get the document body for use on touch devices
		this.body = select(document.body);

		// Verify root DOM element exists
		if (!this.rootModchart.node()) {
			this.warn('Could not find root chart element');
			return false;
		}
		this.initDOM();
		return true;
	}

	/**
	 * Main configuration collection. These properties can be serialized and stored.
	 * @param {Object} params - Collection of custom params passed in to the chart
	 * @returns {Object} Merged collection of custom and default params
	 */
	getDefaultParams(params) {
		const chartParams = {
			/**
			 * use an animated effect to reveal chart panels
			 *  "all" = animate every time data loads
			 *  "first" = animate only the first time data loads
			 *  "staged" = animation will be staged but not immediately run
			 */
			animation: params.animation || null, // "all", "first", "staged"

			/** use an animated effect to reveal the panels on first-time load */
			animationDuration:
				typeof params.animationDuration === 'number'
					? params.animationDuration
					: 1200,

			/** following a pan/zoom event, backfill any historic data gaps with a delta data request */
			backfill:
				typeof params.backfill === 'boolean' ? params.backfill : true,

			/** candle width tolerance - snap to 1px wide candles at this width (0 to disable snapping completely) */
			candleWidthTolerance:
				typeof params.candleWidthTolerance === 'number'
					? params.candleWidthTolerance
					: 1.2,

			/** clip intraday markers to exchange sessions */
			clipSessions:
				typeof params.clipSessions === 'boolean'
					? params.clipSessions
					: true,

			/**
			 * Collapse gaps in XAxis when exchange has an early closure. Note: gaps will still appear
			 * when compared to an exchange that does trade during the closure.
			 */
			collapseAlternateSessionGaps:
				typeof params.collapseAlternateSessionGaps === 'boolean'
					? params.collapseAlternateSessionGaps
					: true,

			/** Configure how the crosshair is displayed. Make sure the object is an object and also not an array. */
			crosshair:
				Object(params.crosshair) === params.crosshair && !Array.isArray(params.crosshair)
					? params.crosshair
					: {
						enabled: false, // Disabled by default
						flagEnabled: false,
						persist: false,
						primaryOnly: false,
						returnClosestCustomData: true,
						showCircle: false,
						showFlagCrosshairDisabled: false,
						snapHorizontalToData: true
					},

			/** Specify a desired currency code (ex. "EUR") if different from the default currency a symbol is priced in. */
			currency: params.currency || null,

			/**
			 * an object containing one or more custom datasets.  datasets should be in the format:
			 * { "mydata": [{date: "2015-01-05", value: 12.4, additionalVal: 14.2, additionalVal2: 41.4, ...}] }
			 * the key of the dataset ("mydata") can be used as the custom indicator's "datasetId" param.
			 */
			customData: params.customData || null,

			/** enable custom dataInterface */
			dataInterface: params.dataInterface || null,

			/** data interval (1, 3, 5, 10) */
			dataInterval: params.dataInterval || null,

			/** data api should return values as normalized figures (percent change) instead of actuals */
			dataNormalized:
				typeof params.dataNormalized === 'boolean'
					? params.dataNormalized
					: false,

			/** data period ("Hour", "Minute", "Day") */
			dataPeriod: params.dataPeriod || null,

			/** data precision - series values will return this number of decimal points */
			dataPrecision: params.dataPrecision || 3,

			/** If dateStart/Stop specified, it will become the visible canvas range even if the chartapi data range is different. */
			dateStart: params.dateStart || null,
			dateStop: params.dateStop || null,

			/** chart api - total days of data to request */
			days: params.days || null,

			/**
			 * Ignore streamed events until we have at least one datapoint for the current day.  This prevents double candlesticks
			 * at market open but also will mean the chart won't stream for up to one interval/period at the beginning of the day. (NXC-690)
			 */
			delayMarketOpenStream:
				typeof params.delayMarketOpenStream === 'boolean'
					? params.delayMarketOpenStream
					: false,

			disableLogMessages:
				typeof params.disableLogMessages === 'boolean'
					? params.disableLogMessages
					: false,

			enableRightAlignFinalLabel:
				typeof params.enableRightAlignFinalLabel === 'boolean'
					? params.enableRightAlignFinalLabel
					: true,

			/** exchangeOffset */
			exchangeOffset:
				typeof params.exchangeOffset === 'number'
					? params.exchangeOffset
					: 0,

			/** data api should return extended sessions along with the regular session */
			extendedHours:
				typeof params.extendedHours === 'boolean'
					? params.extendedHours
					: false,

			/** duration of the premarket session (minutes) */
			extendedHoursDurationPre:
				typeof params.extendedHoursDurationPre === 'number'
					? params.extendedHoursDurationPre
					: 330,

			/** duration of the postmarket session (minutes) */
			extendedHoursDurationPost:
				typeof params.extendedHoursDurationPost === 'number'
					? params.extendedHoursDurationPost
					: 240,

			/** feed selection group (optional) */
			feedGroup: params.feedGroup || null,

			/** Only render lastClose flags on primary indicator */
			lastCloseFlagOnlyPrimary:
				typeof params.lastCloseFlagOnlyPrimary === 'boolean'
					? params.lastCloseFlagOnlyPrimary
					: false,

			/** Allow price flags to overlap (default = true) */
			flagOverlap:
				typeof params.flagOverlap === 'boolean'
					? params.flagOverlap
					: true,

			/** A feature used by the mobile SDK implementation of Modcharts.  Array of lower indicator uids. */
			hiddenLowers: params.hiddenLowers || [],

			/** Ignore the bottom margin of the last panel in sizing calculations */
			ignoreLastPanelBottomMargin:
				typeof params.ignoreLastPanelBottomMargin === 'boolean'
					? params.ignoreLastPanelBottomMargin
					: true,

			/** Ignore the top margin of the lower panels in sizing calculations */
			ignoreLowerPanelTopMargin:
				typeof params.ignoreLowerPanelTopMargin === 'boolean'
					? params.ignoreLowerPanelTopMargin
					: false,

			/** The limit of consecutive 401 requests due to invalid tokens */
			invalidTokenLimit:
				typeof params.invalidTokenLimit === 'number'
					? params.invalidTokenLimit
					: 50,

			/** Default locale file */
			locale: typeof params.locale === 'object'
				? params.locale
				: LocaleEnUs,

			/** Switch to a 2-day chart if on 1-day with only a single datapoint */
			minMarketOpen:
				typeof params.minMarketOpen === 'boolean'
					? params.minMarketOpen
					: true,

			/** normalizeDate - the date which should be used as the chartapi normalize value */
			normalizeDate: params.normalizeDate
				? new Date(params.normalizeDate)
				: null,

			/** normalizeValue - (deprecated) the value which should be used as the normalization starting point */
			normalizeValue: params.normalizeValue,

			/** normalizeValues - the ohlc value which should be used as the normalization starting point */
			normalizeValues: params.normalizeValues,

			/** A ratio of the panel height to the chart width which can be used to size the lower panel height (OPTIONAL) */
			panelHeightToChartWidthRatioLower: params.panelHeightToChartWidthRatioLower
				? Number(params.panelHeightToChartWidthRatioLower)
				: null,

			/** A ratio of the panel height to the chart width which can be used to size the upper panel height (OPTIONAL) */
			panelHeightToChartWidthRatioUpper: params.panelHeightToChartWidthRatioUpper
				? Number(params.panelHeightToChartWidthRatioUpper)
				: null,

			/** optional fixed upper panel height */
			panelHeightUpper: params.panelHeightUpper
				? Number(params.panelHeightUpper)
				: null,

			/** optional fixed lower panel height */
			panelHeightLower: params.panelHeightLower
				? Number(params.panelHeightLower)
				: null,

			/** optional minimum height percentage for upper panel (used in conjunction with panelHeightPctLower) */
			panelHeightPctMinUpper:
				typeof params.panelHeightPctMinUpper === 'number'
					? params.panelHeightPctMinUpper
					: 0.25,

			/** optional lower panel height percentage for fluid-height charts.  ex: 0.1 would mean a tenth of the overall chart height. */
			panelHeightPctLower:
				typeof params.panelHeightPctLower === 'number'
					? params.panelHeightPctLower
					: null,

			/** Enable panel resizing */
			panelResize:
				typeof params.panelResize === 'boolean'
					? params.panelResize
					: false,

			/** which panel(s) should receive an XAxis by default (can be overridden on a per-panel basis) */
			panelXAxis: params.panelXAxis || 'first', // "last", "first", "all", "none"

			/** If set to true, this allows the YAxis width to be auto-calculated to fit all current YAxis labels, overriding the panel.padding.right style. */
			panelYAxisAutoWidth:
				typeof params.panelYAxisAutoWidth === 'boolean'
					? params.panelYAxisAutoWidth
					: false,

			/** number of minutes between fresh data poll (0 to turn off polling) */
			poll: typeof params.poll === 'number' ? params.poll : 0,

			/** (deprecated) Draw to the normalized point on line/fill charts */
			prependNormalizedPoint:
				typeof params.prependNormalizedPoint === 'boolean'
					? params.prependNormalizedPoint
					: false,

			/** enable the price hover emitters */
			hoverIndicator: typeof params.hoverIndicator === 'boolean' ? params.hoverIndicator : false,

			/** request realtime data (is enforced via feed entitlements on server-side) */
			realtime: typeof params.realtime === 'boolean' ? params.realtime : false,

			/**
			 * Flag to render duplicate row labels, even if they are the same as those to the left/right.
			 * Background: this came out of a request to have the same date on the upper row as the lower row, but formatted differently.
			 * For instance, "Monday" (upper) and "5/24" (lower), where "Monday" was just another way to format 5/24.
			 * To achieve this effect, the user can specify somthing like:
			 *    chart.params.locale.labelTemplates.xAxis.doubleRow.hours = { default: '%A', first: '%-m/%-d' }; // 'A%' = 'Monday', '%-m/%-d' = '5/24'
			 *    chart.params.locale.labelTemplates.xAxis.doubleRow.days.intraday = '%-m/%-d'; // '%-m/%-d' = '5/24'
			 * The labeler then processes the current labels according to the rules as usual, based on chart width, number of labels, etc.
			 * In certain circumstances, that previous alteration to the labelTemplates will create duplicate labels in one of the collections, e.g. "hours": ["Monday", "Monday", "Monday"].
			 * So, the renderDuplicateLabels flag will determine how to handle this situation.
			 *   If set to KEEP_LEFT (the default), it will only render the first "Monday" label.
			 *   If set to KEEP_ALL, it will render all "Monday" labels (this will probably never be needed)
			 *   If set to KEEP_RIGHT, it will render the final "Monday" label.
			 * Nearly always you'll want KEEP_LEFT, but KEEP_ALL and KEEP_RIGHT are provided for flexibility.
			 */
			renderDuplicateLabels: params.renderDuplicateLabels || renderDuplicateLabelsCategories.KEEP_LEFT,

			/** Trap keydown events to remove active tool on backspace/delete */
			removeToolByKey:
				typeof params.removeToolByKey === 'boolean'
					? params.removeToolByKey
					: true,

			/** Return an extra datapoint to remove left edge gaps in line and fill markers (CH-1745) */
			returnPreviousPoint:
				typeof params.returnPreviousPoint === 'boolean'
					? params.returnPreviousPoint
					: true,

			/**
			 * date ranges for the timeservice call.  unit is days.  the timeservice call returns
			 * tradable days within a date range, which is used to construct the XAxis ruler.
			 */
			rulerIntradayStart: params.rulerIntradayStart || 31,
			rulerIntradayStop: params.rulerIntradayStop || 3,
			rulerInterdayStart:
				params.rulerInterdayStart || Math.floor(365.25 * 35),
			rulerInterdayStop: params.rulerInterdayStop || 10,

			/** Enable/disable display of all flags */
			showFlags:
				typeof params.showFlags === 'boolean'
					? params.showFlags
					: false,

			/** turn on component series flags */
			showComponentSeriesFlags:
				typeof params.showComponentSeriesFlags === 'boolean'
					? params.showComponentSeriesFlags
					: false,

			/** Show the crosshair at the most recent position after initial chart load and time span changes */
			showCrosshairAfterLoad:
				typeof params.showCrosshairAfterLoad === 'boolean'
					? params.showCrosshairAfterLoad
					: false,

			/** Enable/disable display of last close flags. This will affect all price indicators. */
			showLastCloseFlags:
				typeof params.showLastCloseFlags === 'boolean'
					? params.showLastCloseFlags
					: true,

			/** enable xaxis flag */
			showXAxisFlag:
				typeof params.showXAxisFlag === 'boolean'
					? params.showXAxisFlag
					: false,

			/** major currency flag (NXC-748) */
			showMajorCurrency:
				typeof params.showMajorCurrency === 'boolean'
					? params.showMajorCurrency
					: true,

			/** turn on lunch break labels (DEPRECATED) */
			showLunchBreakLabels:
				typeof params.showLunchBreakLabels === 'boolean'
					? params.showLunchBreakLabels
					: false,

			/** turn on holiday/closure labels */
			showClosureLabels:
				typeof params.showClosureLabels === 'boolean'
					? params.showClosureLabels
					: false,

			/** Show market close labels on the X-axis. Possible values are shown in enums/modcharts. */
			showMarketCloseLabels: params.showMarketCloseLabels
				? params.showMarketCloseLabels
				: null,

			/** Show a vertical legend to the right of the YAxis */
			showVerticalPanelLegend:
				typeof params.showVerticalPanelLegend === 'boolean'
					? params.showVerticalPanelLegend
					: false,

			/** Set the chart size manually */
			size:
				params.size
					? { ...params.size }
					: null,

			/** overall chart style vars */
			style: { ...(params.style || {}) },

			/** styles that are common to multiple components */
			styleCommon: { ...(params.styleCommon || {}) },

			/** primary symbol */
			symbol: (params.symbol && params.symbol.toString()) || '',

			/** array of comparison symbols (deprecated) */
			symbolCompare: params.symbolCompare || [],

			/** allow custom initial tool params */
			tools: { ...(params.tools || {}) },

			/** Enable tool handle snapping */
			toolSnapOHLC:
				typeof params.toolSnapOHLC === 'boolean'
					? params.toolSnapOHLC
					: false,

			/** enable the use of longDoublePress crosshair tool (mobile only) */
			toolReturnsCalculatorEnabled:
				typeof params.toolReturnsCalculatorEnabled === 'boolean'
					? params.toolReturnsCalculatorEnabled
					: false,

			/** enable the use of longPress crosshair tool (mobile only) */
			toolOHLCCrosshairEnabled:
				typeof params.toolOHLCCrosshairEnabled === 'boolean'
					? params.toolOHLCCrosshairEnabled
					: false,

			/** optional usage reporting flag */
			usageReportingAppFeatureType:
				params.usageReportingAppFeatureType || null,


			/** Rely on computed dimensions of panel DOM element to size each panel. */
			usePanelComputedHeight: !!params.usePanelComputedHeight,

			/** Adjust the domain for certain marker types (bar, candle) to avoid truncated bars */
			useXAxisDomainAdjustment:
				typeof params.useXAxisDomainAdjustment === 'boolean'
					? params.useXAxisDomainAdjustment
					: true,

			/**
			 * Enable alternate short month labels on the XAxis
			 */
			xAxisShortMonths:
				typeof params.xAxisShortMonths === 'boolean'
					? params.xAxisShortMonths
					: true,

			/**
			 * Enable single-row mode on the XAxis
			 */
			xAxisSingleRowMode:
				typeof params.xAxisSingleRowMode === 'boolean'
					? params.xAxisSingleRowMode
					: false,

			/**
			 * Pass additional headers to data XHR request.  expects an array in this format:
			 * [{"name": "header-x", "value": "value-1"}, {"name": "header-y", "value": "value-2"}]
			 */
			xhrRequestHeaders: params.xhrRequestHeaders || [],

			/**
			 * Pass cookies in cross-domain requests for chart data
			 * Note: "true" requires MOD.Web.ChartAPI version 0.1.83-beta or higher
			 */
			xhrWithCredentials:
				typeof params.xhrWithCredentials === 'boolean'
					? params.xhrWithCredentials
					: false,

			/** yAxis scale type */
			yAxisScale: params.yAxisScale || 'linear', // "linear", "log"

			/** Defines the side of the panel where the YAxis will be positioned */
			yAxisSide:
				params.yAxisSide || 'right', // 'left', 'right'

			/** Duration (in ms) for zoom transitions */
			zoomDuration:
				!isNaN(params.zoomDuration)
					? params.zoomDuration
					: 350,

			/** Duration (in ms) for the zoom reset transition */
			zoomResetDuration:
				!isNaN(params.zoomResetDuration)
					? params.zoomResetDuration
					: 150,

			/** snap to a domain limit after zoom/pan event */
			zoomReset:
				typeof params.zoomReset === 'boolean' ? params.zoomReset : true,

			/** is the zoomReset domain limited by the dataset or the XAxis ruler? */
			zoomResetBoundary:
				params.zoomResetBoundary || 'ruler', // "ruler", "dataset"

			/** toggle ability to zoom/pan with mouse */
			zoomEnabled:
				typeof params.zoomEnabled === 'boolean'
					? params.zoomEnabled
					: true
		};

		/** api auth mode */
		chartParams.apiAuthMode =
			typeof params.apiAuthMode === 'string'
				? params.apiAuthMode
				: 'default';

		/** custom content-type */
		chartParams.apiContentType =
			params.apiContentType || 'application/json';

		/** apiPath prefix for related API requests (series, save, load) */
		chartParams.apiPath = params.apiPath || 'https://cdn-api.markitdigital.com/apiman-gateway/MOD/chartworks-data/1.0/';

		/** API series action (deprecated) */
		chartParams.apiSeries = params.apiSeries || 'chartapi/series';

		/** location of chartAPI service */
		chartParams.chartAPI =
			params.chartAPI || chartParams.apiPath + chartParams.apiSeries;

		// Update API paths to https so requests don't break
		const apiCheck = ['apiPath', 'chartAPI'];
		apiCheck.forEach(param => {
			if (chartParams[param]) {
				const apiParam = chartParams[param];
				if (/^\/\//.test(apiParam)) { // Does param begin with // instead of https?
					chartParams[param] = `https:${apiParam}`;
				}
			}
		});

		/** constants based on params */
		this.limitIntradayTrading = 31;
		this.limitIntradayCalendar = 30;

		// deprecation notice
		if (chartParams.imgPath) this.warn('imgPath param is deprecated.');

		// Get all params from query string, merge them into chartParams
		if (typeof window === 'object' && window.location.search) {
			// Query string params will override all other params
			const queryParams = new URLSearchParams(window.location.search);

			const parseValue = value => {
				const valueLowerCase = typeof value === 'string'
					? value.toLowerCase()
					: value;

				if (value === true || valueLowerCase === 'true') return true;
				else if (value === false || valueLowerCase === 'false') return false;
				else if (!isNaN(value)) return Number(value);
				return value;
			};

			const updateChartParams = newParams => {
				let breakOutOfLoop = false;
				let jsonParams;

				for (const [key, value] of newParams) {
					switch (key) {
						case 'params':
							// Parse and sanitize JSON string
							jsonParams = JSON.parse(value) || {};
							if (jsonParams.enableQueryParams) delete jsonParams.enableQueryParams;
							if (jsonParams.params) delete jsonParams.params;

							// Add JSON params to chart
							updateChartParams(Object.entries(jsonParams));

							breakOutOfLoop = true;
							break;
						case 'symbol':
							// Save as string
							chartParams[key] = String(value);
							break;
						case 'token':
							// Call setToken
							this.setAuthToken(value);
							break;
						default:
							// Only merge if new value is object
							chartParams[key] = typeof value === 'object' && value !== null
								// Don't nullify other param properties if not passed in
								? merge({}, parseValue(value), chartParams[key])
								: parseValue(value);
					}

					// Don't update any more params after handling 'params' case
					if (breakOutOfLoop) break;
				}
			};

			if (queryParams.get('enableQueryParams')) {
				// Don't add enableQueryParams to params
				queryParams.delete('enableQueryParams');

				updateChartParams(queryParams);
			}
		}

		return chartParams;
	}

	/** FPS benchmark */
	getFPS(sec = 3) {
		const startDate = new Date().getTime();
		let x = 0;

		while (new Date().getTime() - startDate < sec * 1000) {
			this.render();
			x++;
		}

		return `${(x / sec).toFixed(2)} fps`;
	}

	/**
	 * Sets the overall chart size. The initial size is (width-of-node)px X 0px.
	 * During resize, the size is set according to the following priority listed below.
	 * @param {Boolean} setInitialSize - Whether or not to set the initial size
	 */
	setChartSize(setInitialSize = false) {
		/**
		 * Chart sizing priority:
		 * 1. Panel heights
		 * 2. Chart size params
		 * 3. Parent node dimensions
		 * 4. Fallback dimensions
		 */

		// Get root node dimensions
		const rect = this.rootModchart.node().getBoundingClientRect();

		// Set initial chart size so that panel sizes can be properly calculated
		if (setInitialSize) {
			this.size = { height: 0 };

			if (this.params.size) {
				this.size.width = this.params.size.width;
			} else {
				this.size.width = rect.width;
			}
		} else {
			// Is this check even necessary?
			const allPanelsHaveHeights =
				this.panels.length > 0 &&
				this.panels.reduce(
					(status, panel) => status && !!panel.size.height,
					true
				);

			if (
				this.params.usePanelComputedHeight &&
				allPanelsHaveHeights
			) {
				const totalHeight = this.panels.reduce((runningHeight, panel) => {
					return (
						runningHeight +
						panel.size.height +
						panel.size.padding.bottom +
						panel.size.padding.top +
						panel.getPanelMarginBottom() +
						panel.getPanelMarginTop()
					);
				}, 0);

				this.size = {
					height: totalHeight,
					width: rect.width
				};
			} else if (this.params.size) { // Use manually set chart height/width
				this.size = {
					height: this.params.size.height,
					width: this.params.size.width
				};
			} else if (
				this.panels.length &&
				typeof this.params.panelHeightUpper === 'number' ||
				typeof this.params.panelHeightLower === 'number'
			) {
				const sumHeight = this.panels.reduce((total, panel) => total + panel.size.height + panel.size.margin.top + panel.size.margin.bottom + panel.size.padding.top + panel.size.padding.bottom, 0);
				this.size = {
					height: sumHeight,
					width: rect.width
				};
			} else if (rect.height > 0 && rect.width > 0) { // Use root node dimensions to size the chart
				this.size = {
					height: rect.height,
					width: rect.width
				};
			} else { // Warn user that no height could be found, use fallback height
				this.warn('No dimensions were specified for the chart or chart container. Using fallback dimensions.');
				this.size = {
					height: 200,
					width: rect.width
				};
			}
		}
	}

	/** Create DOM elements and some bindings */
	initDOM() {
		// Apply required styles
		this.rootModchart.style('position', 'relative');

		// Make sure chart has required classes
		this.rootModchart.classed('modcharts-root modcharts-noselect', true);

		this.setChartSize(true);

		// Add root messages element.  This is an empty element where content can be appended, like "Chart not available".
		this.rootMessages = this.rootModchart
			.append('div')
			.attr('class', 'modcharts-rootmessages');

		// This is a blanket element to track mouse/touch events over the entire chart and all its panels.
		this.rootMouse = this.rootModchart
			.append('div')
			.attr('class', 'modcharts-rootmouse')
			.attr('pointer-events', 'all')
			.style('width', `${this.size.width}px`)
			.style('height', `${this.size.height}px`)
			.style('position', 'absolute')
			.style('background-color', 'rgba(0, 0, 0, 0)');

		// Add resize handler
		this._resizeHandler = () => { this.resizeHandler(); };
		window.addEventListener('resize', this._resizeHandler);

		// Check for optional live debugging flag
		this.updateDebugReference();

		// Initialize zoom
		this.zoom = zoomUtil.createZoom(this);
		this.zoomUtil = zoomUtil;
	}

	/** handle resizing of the window */
	resizeHandler() {
		// an immediate resize/render, followed by delayed resize/render, seems
		// to work more reliably cross-device than a single event.
		this.resize();
		this.renderQueue();

		window.clearTimeout(this.resizeTimeout);

		this.resizeTimeout = window.setTimeout(() => {
			this.resize();
			this.renderQueue();
		}, 15);
		this.eventEmitter.emit(eventTypes.WINDOW_RESIZE);
	}

	/**
	 * tear down js and dom in preparation for the chart instance to be removed
	 * @method destroy
	 */
	destroy() {
		// cancel any active xhr
		if (this.xhrCancelSource) this.xhrCancelSource.cancel();
		this.dataXHR = null;

		// remove panel dom and js
		this.removePanels();

		// null the bigger stored objects
		this.state = null;
		this.params = null;
		this.timeService = null;
		this.exchangeDates = null;
		this.data = null;
		this.dataPrimary = null;

		// remove root elements and unregister event handlers
		this.rootMouse.remove();
		this.rootMessages.remove();

		// unregister resize handler
		window.removeEventListener('resize', this._resizeHandler);

		// unregister any emitter listeners
		this.eventEmitter.removeAllListeners();

		// empty the root element contents
		if (this.rootModchart.node()) this.rootModchart.node().innerHTML = '';
	}

	/** Track temporary app states */
	getChartState() {
		return {
			/**
			 * optional X-MOD-ACCESS-TOKEN header to be added to ajax request headers.  use setAccessToken() to set.
			 * used when accessing an ApiAuthModule-based data API directly, bypassing the APIMAN gateway.
			 */
			accessToken: null,

			/**
			 * optional OAuth bearer token to be added to ajax request headers.  use setAuthToken() to set.
			 * used when accessing a data API through the APIMAN gateway.
			 */
			authToken: null,

			/** number of times data API has been loaded */
			dataLoadCount: 0,

			/** in debug mode (trigged by ?..modchartsdebug..=on or #..modchartsdebug..=on) */
			debug: null,

			/** Whether or not normalized point domain adjustment occurred */
			didNormalizedPointDomainAdjustment: false,

			/** the currently-dragged tool before mouseup */
			dragTool: null,

			/** in the middle of hovering over a panel resize bar */
			hoverResizePanel: null,

			/** Used to track initial domain adjustment that takes places before the normalized point domain adjustment */
			initialDomainAdjustment: [0, 0],

			/** Initial view zoom transform, to be used when resetting the chart to the initial view */
			initialTransform: null,

			/** A count of consecutive invalid token requests */
			invalidTokenCount: 0,

			/** Flag for if chart is intraday */
			isIntraday: false,

			/** Save the present primary frame data to be used in calculating domain transforms */
			presentDataPrimary: null,

			/** the panel currently being resized (the one below the currently-active resize bar) */
			resizePanel: null,

			/** current y value of mouse during panel resizing */
			resizePanelY: null,

			/** Show the crosshair at the most recent position after chart load */
			showCrosshairAfterLoad: true,

			/**
			 * if we're about to start drawing a tool or in the middle of drawing it, this will contain the tool's type (eg. "line")
			 * note - this does not affect the selected state of the tool.
			 */
			toolmode: null,

			/** the currently-active tool object */
			tool: null,

			/** Store for intraday session information */
			typicalSessions: null,

			/**
			 * Flag to proceed with updating present scale domain. This should take place when
			 * exchangeDates gets updated because the exchangeDates indices will change.
			 */
			updateXAxisPresentScaleDomain: false,

			/** Flag to use the dataPrimary collection when updating the xAxis present scale domain */
			useDataPrimaryPresentScaleUpdate: false,

			/** Save the present xAxis scale to be used in calculating domain transforms */
			xAxisPresentScale: null,

			/** in the middle of a zoom event */
			zooming: null
		};
	}

	/** Adds modcharts reference to window context for live debugging */
	updateDebugReference() {
		let isDebug = false;

		// Check query string for debug flag
		if (this.getParameterByName('..modchartsdebug..') === 'on') {
			isDebug = true;
		} else if (document.location.hash && document.location.hash.length) {
			// check hash values for debug flag
			const hash = window.location.hash.substring(1).split('=');

			if (
				hash.length === 2 &&
				hash[0] === '..modchartsdebug..' &&
				hash[1] === 'on'
			) {
				isDebug = true;
			}
		}

		if (isDebug) {
			if (!window.ModchartsDebug) window.ModchartsDebug = [];

			window.ModchartsDebug.push(this);

			this.state.debug = true;
		}
	}

	/** timeFormat with locale rules */
	timeFormat(specifier) {
		if (!this.state.locale) this.state.locale = formatLocale(this.locale);
		if (!this.state.timeFormatDefaultLocale) {
			this.state.timeFormatDefaultLocale = timeFormatDefaultLocale(this.locale);
		}

		return this.state.timeFormatDefaultLocale.format(specifier);
	}

	/** Return position information for chart elements */
	getChartDimensions() {
		const output = { panels: [] };

		this.panels.forEach(panel => {
			output.panels.push({
				top: panel.size.top + panel.size.padding.top,
				left: panel.size.padding.left,
				height: panel.size.height,
				width: panel.size.width
			});
		});

		return output;
	}

	/**
	 * return ratio between devicepixelratio and backingstoreratio
	 * http://www.html5rocks.com/en/tutorials/canvas/hidpi/
	 */
	getScreenRatio() {
		if (!this.panels.length) {
			return 1;
		}

		const rootContext = this.panels[0].rootContext;
		const devicePixelRatio = window.devicePixelRatio || 1;
		const backingStoreRatio =
				rootContext.webkitBackingStorePixelRatio ||
				rootContext.mozBackingStorePixelRatio ||
				rootContext.msBackingStorePixelRatio ||
				rootContext.oBackingStorePixelRatio ||
				rootContext.backingStorePixelRatio ||
				1;

		return devicePixelRatio / backingStoreRatio;
	}

	/**
	 * cross-browser fullscreen
	 * @method toggleFullScreen
	 */
	toggleFullScreen() {
		const element = document.body;

		// Supports most browsers and their versions.
		const requestMethod =
			element.requestFullScreen ||
			element.webkitRequestFullScreen ||
			element.mozRequestFullScreen ||
			element.msRequestFullscreen;

		if (requestMethod) {
			// Native full screen.
			requestMethod.call(element);
		}
	}

	/** Send warning to browser console */
	warn(msg) {
		if (!this.params.disableLogMessages) console.warn(msg);
	}

	/**
	 * return panel currently under mouse
	 * @method getActivePanel
	 * @param {Number[]} mouse - Mouse position information [x, y]
	 */
	getActivePanel(mouse) {
		let activePanel = null;

		for (const panel of this.panels) {
			if (
				!activePanel &&
				panel.isWithin(mouse)
			) {
				activePanel = panel;
				break;
			}
		}

		return activePanel;
	}

	/** get primary indicator */
	getPrimaryIndicator() {
		return this.primaryIndicator;
	}

	get primaryIndicator() {
		return this.panels.length && this.panels[0].indicators.length
			? this.panels[0].indicators[0]
			: null;
	}

	/**
	 * Sets the markerType of the primary price indicator
	 * @param {priceMarkerTypes} type - The marker type for the price indicator
	 */
	setPriceMarkerType(type) {
		this.primaryMarkerType = type;
	}
	set primaryMarkerType(type) {
		this.primaryIndicator.markerType = type;
	}

	get primaryMarkerType() {
		return this.primaryIndicator.markerType;
	}

	set primaryOhlcType(type) {
		this.primaryIndicator.ohlcType = type;
	}

	/** Set all panel domains to the first panel's domain */
	normalizePanelDomains() {
		const firstPanel = this.panels[0];
		const firstDomain = firstPanel.xAxis.primaryScale.domain();

		this.panels.forEach(panel => {
			if (panel !== firstPanel) panel.xAxis.primaryScale.domain(firstDomain);
		});
	}

	/** snap date to the nearest following interval-adjusted date */
	getNextIntervalMinute(date) {
		while (date.getMinutes() % this.params.dataInterval !== 0) {
			date.setMinutes(date.getMinutes() + 1);
		}

		return date;
	}

	/**
	 * receive a datapoint from external streamserver.  this will either modify the last chart
	 * datapoint or create a new one.
	 *
	 * the input datapoint object should contain these properties:
		{
			"date": 1404230760000,
			"last": 26.4,
			"symbol": "36276"
		}
	* optionally high and low and open can also be passed.
	* open will only be used when creating new datapoints.
		{
			"date": 1404230760000,
			"last": 26.4,
			"high": 26.72,
			"low": 24.23,
			"open": 24.23,
			"symbol": "36276"
		}
	* @method onStreamTrade
	*/
	onStreamTrade(datapoint) {
		// null checks
		if (
			!this.data ||
			!this.data[this.params.symbol] ||
			!datapoint ||
			!datapoint.date ||
			!datapoint.last
		) {
			return;
		}

		// ignore streams that don't match the current symbol
		if (datapoint.symbol && datapoint.symbol !== this.params.symbol) {
			return;
		}

		// ignore if streaming is currently suppressed
		if (this.isPollStreamSuppressed()) {
			return;
		}

		const localDate = new Date(datapoint.date);
		const panelNormalized = this.panels[0].isNormalized;
		let newDate;

		// get adjusted date
		if (!this.state.isIntraday) {
			newDate = new Date(localDate);
			newDate.setHours(0);
			newDate.setMinutes(0);
			newDate.setSeconds(0);
		} else {
			// adjust for local tz offset
			localDate.setMinutes(
				localDate.getMinutes() + localDate.getTimezoneOffset()
			);

			newDate = new Date(
				this.exchangeDates[this.closestExchangeIndex(localDate)]
			);

			if (this.params.dataInterval > 1) {
				newDate = this.getNextIntervalMinute(newDate);
			}
		}

		let newClose = Number(datapoint.last);
		const openDate = this.getExchangeOpenDate();
		const datasetOHLC = this.dataPrimary;
		const prevOHLC = datasetOHLC[datasetOHLC.length - 1];
		const prevDate = prevOHLC.date;
		const diffSec = Number(((newDate - prevDate) / 1000).toFixed(2));
		const period = this.params.dataPeriod;

		// if panel is normalized, convert value
		if (panelNormalized) {
			newClose = this.getNormalizedFromActual(newClose);
		}

		if (newDate >= prevDate) {
			if (openDate && newDate - openDate < 1000) {
				// skip datapoint if < 1 second from market open - since streamserver rounds to the nearest
				// second these events will appear exactly on market open, which will overlap the last bar of
				// the previous day if allowed to render.
				if (this.state.debug) {
					console.info('SKIP, mkt open');
				}
			} else if (newDate.getDate() !== prevDate.getDate()) {
				// prevent erroneous snapping to ruler point on the following trading day, which can happen
				// at market close
				if (this.state.debug) {
					console.info('SKIP, late', newDate.getDate(), prevDate.getDate());
				}
			} else if (
				(period === 'Minute' &&
					diffSec < 60 * this.params.dataInterval) ||
				(period === 'Day' && diffSec < 86400) ||
				(period === 'Hour' && diffSec < 3600) ||
				(period === 'Week' && diffSec < 604800) ||
				(period === 'Month' && diffSec < 2678400)
			) {
				// if within current interval, merge new point into our last existing point.
				// note we ignore open here, since that's the open from a different, smaller rollup
				// (eg. a rollup of the last 250ms vs the fixed 1-minute buckets the chart uses)
				if (datapoint.high && datapoint.low) {
					// support high and low if available
					prevOHLC.close = newClose;
					prevOHLC.high = Math.max(
						prevOHLC.high,
						Number(datapoint.high)
					);
					prevOHLC.low = Math.min(
						prevOHLC.low,
						Number(datapoint.low)
					);
				} else {
					// otherwise roll up high and low manually
					prevOHLC.close = newClose;
					prevOHLC.high = Math.max(prevOHLC.high, newClose);
					prevOHLC.low = Math.min(prevOHLC.low, newClose);
				}

				// render the update
				this.renderQueue();

				if (this.state.debug) {
					console.info(`REPLACE, last=${newClose}`);
				}
			} else {
				// date is beyond the interval of the last datapoint, so create a brand new point
				datasetOHLC.push({
					date: newDate,
					open: Number(datapoint.open || newClose),
					close: newClose,
					high: Number(datapoint.high || newClose),
					low: Number(datapoint.low || newClose)
				});

				// render the update
				this.renderQueue();

				if (this.state.debug) {
					console.info(`APPEND, diff sec=${diffSec}`);
				}
			}
		} else {
			// skip when streamed date is earlier than last known dataset date, this can happen at
			// the beginning of a stream due to timestamp differences between streamserver and quoteserver
			if (this.state.debug) {
				console.info('SKIP, early');
			}
		}

		if (this.state.debug) {
			console.log('   stream date', localDate);
			console.log('prev ohlc date', prevDate);
			console.log(' mkt open date', openDate);
			console.log('      new date', newDate);
			console.log('      diff sec', diffSec);
			console.log('');
		}
	}

	/**
	 * get market open date for the current day in local exchange time
	 */
	getExchangeOpenDate() {
		const d = new Date();
		const openDate = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);

		try {
			const typical = this.timeService.typicalSessions[d.getDay()];
			const exchangeId = this.xref[this.params.symbol].exchangeId;

			openDate.setMinutes(typical.rulerSessions[exchangeId].open);
		} catch (e) {
			return null;
		}

		return openDate;
	}

	/**
	 * @method startTestStream
	 */
	startTestStream(dateStart, ms) {
		if (!dateStart) {
			dateStart = new Date();
			dateStart.setDate(dateStart.getDate());
			dateStart.setHours(13 - dateStart.getTimezoneOffset() / 60);
			dateStart.setMinutes(30);
			dateStart.setSeconds(0);
			dateStart.setMilliseconds(250);
		}

		let lastTrade = this.dataPrimary[this.dataPrimary.length - 1].close;
		let tradeCount = 0;
		const speed = 4;

		this.stopTestStream();

		this.testStreamInterval = window.setInterval(() => {
			lastTrade = lastTrade * (Math.random() > 0.5 ? 1.0001 : 0.9999);

			this.onStreamTrade({
				date: new Date(dateStart.setMilliseconds(speed * tradeCount++)),
				last: lastTrade
			});
		}, ms || 1000);
	}

	/**
	 * @method stopTestStream
	 */
	stopTestStream() {
		if (this.testStreamInterval) {
			window.clearInterval(this.testStreamInterval);
		}
	}

	/**
	 * Empty out the primary domain. This is usually done ahead of a data request that will require
	 * an entirely new timeframe or symbol with different trading days. The domain will then be
	 * reconstructed against the new data in getDefaultDomain.
	 * @param {boolean} updateXAxisPresentScaleDomain - Flag to update the xAxis present scale domain
	 * @param {boolean} useDataPrimaryPresentScaleUpdate - Flag to use the present dataPrimary collection for the present scale update
	 */
	resetDomain(updateXAxisPresentScaleDomain = true, useDataPrimaryPresentScaleUpdate = false) {
		if (this.panels.length) {
			this.panels[0].xAxis.primaryScale.domain([0, 0]);
			this.state.updateXAxisPresentScaleDomain = updateXAxisPresentScaleDomain;
			this.state.useDataPrimaryPresentScaleUpdate = useDataPrimaryPresentScaleUpdate;
		}
	}

	/**
	 * @method setLocale
	 * @param {Object} locale - The new locale object to be merged with the default locale.
	 *
	 * This method sets the locale for the application by merging the provided locale object
	 * with the default English (US) locale. It then updates the internal state and parameters
	 * with the new locale, and regenerates the labels for the panels if they exist.
	 */
	setLocale(locale) {
		const newLocale = merge({}, LocaleEnUs, locale);
		this.locale = newLocale;
		this.params.locale = newLocale;
		this.state.locale = null;
		this.state.timeFormatDefaultLocale = null;

		if (this.panels && this.panels.length) {
			// regenerate labels
			this.panels[0].xAxis.labeler._createLabels();
		}
	}

	/**
	 * @method setAuthToken
	 * sets an authorization token to be added to ajax request headers
	 * @param {string} token
	 */
	setAuthToken(token) {
		this.state.authToken = token;
	}

	/**
	 * @method setAccessToken
	 * sets an authorization token to be added to ajax request headers
	 */
	setAccessToken(token) {
		this.state.accessToken = token;
	}

	/**
	 * Changes the mouse cursor to the type passed in by updating the chart classes
	 */
	setCursor(id) {
		if (this.cursor === id) return;

		this.cursor = id;
		if (!this.rootModchart.classed(`modcharts-cursor-${id}`)) {
			// remove old class
			Object.values(cursorTypes).forEach(cursorType => {
				this.rootModchart.classed(`modcharts-cursor-${cursorType}`, false);
			});

			this.rootModchart.classed(`modcharts-cursor-${id}`, true);
		}
		this.eventEmitter.emit(eventTypes.CURSOR_CHANGE, { cursor: this.cursor });
	}

	/**
	 * (Deprecated CH-1504)
	 * Clear out old symbolCompare values and indicators and add new ones
	 * @param {Number[]} newSymbolCompare - Array of new comparison symbol XID's
	 */
	setSymbolCompare(newSymbolCompare, compareStyles = []) {
		// symbolCompare should be an array
		if (typeof newSymbolCompare !== 'object') {
			this.warn('Invalid comparison symbols');
			return;
		}

		const { symbolCompare } = this.params;

		// Change XID's to strings
		newSymbolCompare = newSymbolCompare.map(s => String(s));

		// Remove symbol from this.params.symbolCompare if not in new symbolCompare collection
		const removedSymbols = [];
		forEachRight(symbolCompare, (symbol, index) => {
			if (!newSymbolCompare.includes(symbol)) {
				const removedSymbol = symbolCompare.splice(index, 1)[0];
				removedSymbols.push(removedSymbol);

				// Also remove any price indicators associated with this symbol
				this.panels.forEach(panel => {
					for (const i of panel.indicators) {
						if (
							i.params.symbol &&
							i.params.symbol === removedSymbol
						) {
							panel.removeIndicator(i);
						}
					}
				});
			}
		});

		// Add new comparison symbols in to collection
		const newIndicators = [];
		newSymbolCompare.forEach((symbol, index) => {
			if (!symbolCompare.includes(symbol)) {
				symbolCompare.push(symbol);

				// Add new indicator to collection
				newIndicators.push({
					id: 'price', // Consider switching to ENUM value
					style: compareStyles[index] || {},
					symbol
				});
				this.eventEmitter.emit(eventTypes.COMPARISON_ADD, { issueId: symbol });
			}
		});

		// Add new indicators if upper panel exists
		if (this.panels[0])	this.panels[0].addIndicators(newIndicators);

		if (removedSymbols.length || newIndicators.length) {
			// Reset the domain since the new request can have different exchangeDates and will make the old domain invalid.
			// Update the domain for the present xAxis scale since the old exchangeDates indicies are no longer valid (CH-1719)
			this.resetDomain(true, true);
		}
	}

	/**
	 * Setter for the dateStart/dateStop chart params. Input dates should be parseable as JS dates.
	 * @param {Date} dateStart - Starting date of range
	 * @param {Date} dateStop - Ending date of range
	 */
	setDateRange(dateStart, dateStop) {
		// Transform arguments into JS dates
		dateStart = new Date(dateStart);
		dateStop = new Date(dateStop);

		// Enforce that start is always earlier than stop
		let minDate = new Date(Math.min(dateStart, dateStop));
		let maxDate = new Date(Math.max(dateStart, dateStop));

		const dateLimit = new Date().setDate(
			new Date().getDate() - this.params.rulerInterdayStart
		);

		minDate = new Date(Math.max(minDate, dateLimit));
		maxDate = new Date(Math.min(maxDate, new Date()));

		// adjust for local tz offset
		minDate.setMinutes(minDate.getMinutes() + minDate.getTimezoneOffset());
		maxDate.setMinutes(maxDate.getMinutes() + maxDate.getTimezoneOffset());

		// custom intraday ranges should extend to midnight of max day (NXC-572)
		const limitIntradayCalendarMs = this.limitIntradayCalendar * 1000 * 60 * 60 * 24;
		if (maxDate.getTime() - minDate.getTime() <= limitIntradayCalendarMs) {
			maxDate.setHours(0, 0, 0, 0);
			maxDate.setDate(maxDate.getDate() + 1);
		}

		// Track if crosshair should be shown after load (if proper param is set)
		this.state.showCrosshairAfterLoad = true;

		// Update time span tracking
		this.params.dateStart = minDate;
		this.params.dateStop = maxDate;
		this.params.days = null;

		// Reset domain - will be repopulated next time getDefaultDomain runs
		this.resetDomain();

		// Clear the initial transform since a custom date range has been set and it will need to be redefined
		this.state.initialTransform = null;
	}

	/**
	 * Update the number of days to be used when requesting data
	 * @param {number} days - The number of days to be used
	 */
	setDays(days) {	this.days = days; }
	set days(days) {
		// Track if crosshair should be shown after load (if proper param is set)
		if (this.params.days !== days) this.state.showCrosshairAfterLoad = true;

		this.resetDomain(false); // Don't update the present xAxis scale

		// Update time span tracking
		this.params.dateStart = null;
		this.params.dateStop = null;
		this.params.days = days;

		this.eventEmitter.emit(eventTypes.TIME_SERIES_SET, {
			days: this.params.days,
			interval: this.params.dataInterval,
			period: this.params.dataPeriod,
			updatedProperty: 'days'
		});
	}

	/**
	 * Update the data interval to be used when requesting data
	 * @param {number} interval - The interval to be used
	 */
	setDataInterval(interval) {	this.dataInterval = interval; }
	set dataInterval(interval) {
		this.params.dataInterval = interval;
		this.eventEmitter.emit(eventTypes.TIME_SERIES_SET, {
			days: this.params.days,
			interval: this.params.dataInterval,
			period: this.params.dataPeriod,
			updatedProperty: 'interval'
		});
	}

	/**
	 * Update the data period to be used when requesting data ('Minute', 'Hour', etc.)
	 * @param {string} period - The period to be used
	 */
	setDataPeriod(period) { this.dataPeriod = period; }
	set dataPeriod(period) {
		this.params.dataPeriod = period;
		this.eventEmitter.emit(eventTypes.TIME_SERIES_SET, {
			days: this.params.days,
			interval: this.params.dataInterval,
			period: this.params.dataPeriod,
			updatedProperty: 'period'
		});
	}

	/**
	 * set new primary symbol and reset price indicators params.symbol
	 * @param {string} symbol - The XID for the current selected symbol
	 */
	setSymbol(symbol) { this.symbol = symbol; }
	set symbol(symbol) {
		symbol = String(symbol || '');

		if (this.params.symbol === symbol || !this.panels.length) return;

		this.resetDomain();

		// Update the zoom center due to xAxis changing
		this.state.updateZoomCenter = true;

		let i;
		const ind = this.panels[0].indicators;

		for (i = ind.length - 1; i >= 0; i--) {
			if (ind[i].params.symbol === symbol) {
				// remove old comparison if it is of the new symbol
				this.panels[0].removeIndicator(ind[i]);
			} else if (ind[i].params.symbol === this.params.symbol) {
				// switch older primary symbol in applicable indicators to the new symbol
				ind[i].params.symbol = symbol;
			}
		}

		const index = this.params.symbolCompare.indexOf(symbol);

		if (index > -1) this.params.symbolCompare.splice(index, 1);

		this.eventEmitter.emit(eventTypes.SYMBOL_UPDATE, { new: symbol, old: this.params.symbol });
		this.params.symbol = symbol;
	}

	/**
	 * setter for params.extendedHours
	 */
	setExtendedHours(isEnabled) {
		if (typeof isEnabled === 'boolean') {
			this.params.extendedHours = isEnabled;
			this.resetDomain();
		}
	}

	/**
	 * setter for params.extendedHoursDurationPre/Post
	 */
	setExtendedHoursDuration(pre, post) {
		this.params.extendedHoursDurationPre = pre;
		this.params.extendedHoursDurationPost = post;
		this.resetDomain();
	}

	/**
	 * Returns style info for the given selector from the overall styleMap
	 * @param {string} selector - Selector to use when getting style (eg. 'panel.padding.right')
	 * @return {string|number} The style found in the styleMap
	 */
	getStyle(selector) {
		const val = get(this.styleMap, selector);
		if (val === undefined) {
			this.warn(`Style not found for selector: ${selector}`);
			return val;
		}
		return deepClone(val);
	}

	/**
	 * get all datasets for a given symbol and data type (price, sma etc.)
	 * one use of this is to grab the first price dataset for the primary chart symbol so that a legend can display OHLC info.
	 * @method getDataByType
	 * @returns {array}
	 */
	getDataByType(symbol, type, symbolData) {
		if (!this.data) return [];

		const indicators = [];

		if (!symbolData) {
			symbolData = this.data[symbol || this.params.symbol] || {};
		}

		const data = symbolData[type] || {};
		Object.values(data).forEach(val => {
			indicators.push(val);
		});

		return indicators;
	}

	/**
	 * add a new panel object to panels collection
	 * @param {object} params
	 * @method addPanel
	 * @return {Panel}
	 */
	addPanel(params = {}) {
		const panel = new Panel();

		this.panels.push(panel);

		panel.init(this, params);

		this.combineXAxes();

		if (!params.size || !params.size.heightPct) {
			this.updatePanelHeightPercents();
		}

		return panel;
	}

	/**
	 * remove panel object from panels collection
	 * @method removePanel
	 * @param {Panel} panel
	 */
	removePanel(panelToRemove) {
		if (panelToRemove && panelToRemove.remove) {
			for (const panel of this.panels) {
				if (panel === panelToRemove) {
					const { uid } = panel.params;
					panelToRemove.remove();
					this.panels = this.panels.filter(p => p.params.uid !== uid);
					break;
				}
			}

			this.combineXAxes();
			this.updatePanelHeightPercents();
		}
	}

	/**
 * Updates the height percentage (heightPct) for each panel in the chart. This function adjusts the relative height
 * of each panel based on the total number of panels and specific height percentage parameters set for upper and lower panels.
 *
 * There are two scenarios for setting the panel heights:
 * 1. Custom height percentages: If custom lower panel height percentages are specified and there are multiple panels,
 *    the function calculates the upper panel's height as the remaining space.
 * 2. Default height distribution: If no custom settings are provided, the upper panel is assigned double the height
 *    percentage of each lower panel.
 *
 * The function also enforces minimum height constraints to ensure that panels are not disproportionately small.
 */
	updatePanelHeightPercents() {
		let isUpper = false;
		const numPanels = this.panels.length;
		let lowerPct = 0.0;
		let upperPct = 0.0;

		if (this.params.panelHeightPctLower && numPanels > 1) {
			// Use custom lower panel height percentage
			lowerPct = this.params.panelHeightPctLower;
			upperPct = 1.0 - lowerPct * (this.panels.length - 1);

			// Enforce a minimum height percentage for the upper panel
			if (upperPct < this.params.panelHeightPctMinUpper) {
				upperPct = this.params.panelHeightPctMinUpper;
				lowerPct = (1.0 - upperPct) / (this.panels.length - 1);
			}

			// Set the height percentage for each panel
			this.panels.forEach(panel => {
				panel.size.heightPct = panel.isUpper ? upperPct : lowerPct;
			});
		} else {
			// Default case: adjust upper panel height relative to lower panels using upperToLowerRatio

			// Variable to adjust the ratio between upper and lower panel heights in the default case (higher == taller upper panel)
			const upperToLowerRatio = 4;

			lowerPct = 1.0 / (numPanels + upperToLowerRatio - 1);
			upperPct = lowerPct * upperToLowerRatio;

			this.panels.forEach(panel => {
				isUpper = panel.isUpper;

				if (isUpper && numPanels === 1) {
					panel.size.heightPct = 1;
					panel.params.size.heightPct = null;
					return false;
				}

				panel.size.heightPct = isUpper ? upperPct : lowerPct;
			});
		}
	}


	/**
	 * modify the padding of all panels so that only
	 * specified panels contains an x-axis
	 * @method combineXAxes
	 */
	combineXAxes() {
		this.panels.forEach(panel => {
			panel.size.padding.bottom = panel.xAxis.hasAxis()
				? panel.params.style.padding.bottom
				: 0;
		});
	}

	/**
	 * Remove all indicators by panel type (upper/lower)
	 */
	removeIndicatorsByPanelType(panelType) {
		if (panelType === 'lower') {
			this.removeLowerIndicators();
		} else if (panelType === 'upper') {
			this.removeUpperIndicators();
		}
	}

	/**
	 * Remove all indicators by id ("sma", "bollinger", etc.)
	 */
	removeIndicatorsById(indicatorId) {
		// remove matching lowers
		this.removeLowerIndicators({ indicatorId });

		// remove matching uppers
		this.removeUpperIndicators({ indicatorId });
	}

	/**
	 * Remove all upper indicators (except the primary price indicator)
	 */
	removeUpperIndicators(args = {}) {
		const startCompareLen = this.comparisons.length;
		// remove indicators from the first panel in reverse order
		[...this.panels[0].indicators]
			.reverse()
			.forEach(indicator => {
				if (indicator !== this.panels[0].indicators[0]) {
					if (
						!args.indicatorId ||
						args.indicatorId === indicator.params.id
					) {
						this.panels[0].onClickRemoveLegendIndicator(indicator, {
							noLoad: true
						});
					}
				}
			});

		if (this.comparisons.length !== startCompareLen) {
			// reconstruct axis via new API call instead of just re-rendering. this is sometimes needed
			// if we've removed comparison symbols on exchanges with different sessions.
			this.resize();
			this.loadData();
		} else {
			// just re-render if no comparisons were removed
			this.render();
		}
	}

	/**
	 * Remove all lower indicators
	 */
	removeLowerIndicators(args = {}) {
		// remove lower panels in reverse order
		[...this.panels]
			.reverse()
			.forEach(panel => {
				if (panel.index > 0) {
					// remove indicators from this panel in reverse order
					panel.indicators
						.slice()
						.reverse()
						.forEach(indicator => {
							if (
								!args.indicatorId ||
								args.indicatorId === indicator.params.id
							) {
								panel.onClickRemoveLegendIndicator(indicator);
							}
						});
				}
			});

		// recalculate panel heights and re-render
		this.updatePanelHeightPercents();
		this.resize();
		this.render();
	}

	/** Set loading state to true. Adds class to root chart element. */
	loadingStart() {
		this.clearMessage();
		this.rootModchart.classed('modcharts-root-loading', true);
		this.eventEmitter.emit(eventTypes.DATA_LOAD_START);
	}

	/** Set loading state to false. Removes class from root chart element. */
	loadingStop() {
		if (this.rootTools) this.rootTools.show();
		this.rootModchart.classed('modcharts-root-loading', false);
		this.eventEmitter.emit(eventTypes.DATA_LOAD_STOP);
	}

	/**
	 * Get specified value from URL query string
	 * @param {string} name - Name of variable to search for
	 * @param {string} search - Query string to search through
	 * @return {boolean|string} Value of variable from query string or false
	 */
	getParameterByName(name, search = window.location.search) {
		const queryParams = new URLSearchParams(search);
		return queryParams.has(name) && queryParams.get(name);
	}

	/**
	 * Return a collection of what we deem "comparison indicators" (CH-1504, CH-3897)
	 */
	get comparisons() {
		return this.panels[0].indicators.filter((i, idx) => {
			return idx > 0 &&
				i.params.id === 'price' &&
				i.params.symbol &&
				String(i.params.symbol) !== String(this.params.symbol); // edge case where comparison symbol is a number
		});
	}

	/** Recalculate panel dimensions and reset the rootMouse element */
	resize() {
		// Set the chart size here to catch if switching from panel computed heights
		this.setChartSize();

		this.combineXAxes();

		this.panels.forEach(panel => panel.resize());

		// Set the chart size here to reflect new panel sizes
		this.setChartSize();

		// DOM update - rootMouse and rootMessages
		const domWidth = this.size.width;
		const domHeight = this.size.height;
		this.rootMouse.style('width', `${domWidth}px`);
		this.rootMouse.style('height', `${domHeight}px`);
		this.rootMessages.style('width', `${domWidth}px`);
		// Set the height of the rootMessages element to the height of the first panel
		if (this.panels.length) this.rootMessages.style('height', `${this.panels[0].size.height}px`);

		// DOM update - rootModchart (only needed if panel heights are manually set)
		if (
			typeof this.params.panelHeightUpper === 'number' ||
			typeof this.params.panelHeightLower === 'number'
		) {
			this.rootModchart.style('height', `${this.size.height}px`);
		}

		this.eventEmitter.emit(eventTypes.RESIZE);
	}

	/**
	 * filter main dataset by current timerange
	 * update all panel scales against primary scale
	 * render panels
	 */
	render(args = {}) {

		if (!this.panels.length) return;
		if (!this.dataPrimary) return;
		if (!this.exchangeDates || !this.exchangeDates.length) return;

		// update panels x domain / ranges
		this.normalizePanelDomains();

		// update panels y domain / range
		this.panels.forEach(panel => panel.configureAxes());

		// generate subset of labels to draw
		const filteredLabels = this.panels[0].xAxis.labeler.filterLabels();

		const eventEmitterData = { domain: this.panels[0].xAxis.primaryScale.domain() };

		// Check if dataFrame should be updated
		const isCustomData = !!this.params.customData;
		const data = isCustomData ? this._getCustomFrameData() : this._getFrameData();

		if (
			(data[this.params.symbol]?.price) ||
			data[this.primaryIndicator.params.datasetId]
		) {
			// Store off data subset for quick lookups later (crosshair and crosshair tool use this)
			const firstDataset = isCustomData
				? data[this.primaryIndicator.params.datasetId]
				: this.getFirstDataset(data[this.params.symbol].price);
			this.dataFrame = firstDataset;
			this.eventEmitter.emit(eventTypes.DATA_FRAME_UPDATE);

			// Simulate mouse moving at the right edge of the chart so that the crosshair will appear after chart load
			if (
				this.panels.length &&
				this.params.showCrosshairAfterLoad &&
				this.state.showCrosshairAfterLoad
			) {
				this.state.showCrosshairAfterLoad = false;

				this.eventEmitter.emit(eventTypes.PANEL_MOUSE_MOVE, {
					mouse: [
						this.panels[0].size.width - 1, // Right edge of upper panel
						this.panels[0].size.height / 2 // Middle of upper panel
					],
					activePanel: this.panels[0]
				});
			}

			// Add primary data from chart frame to event emitter
			eventEmitterData.dataFrame = firstDataset;
		}

		// empty out panels in preparation for new content
		this.clearPanels();

		// Render panels
		this.panels.forEach(panel => panel.render(data, filteredLabels, args.clipWidth));

		this.eventEmitter.emit(eventTypes.RENDER, eventEmitterData);

		if (this.eventEmitter.listeners(eventTypes.DATA_PRESENT).length > 0) {
			this.eventEmitter.emit(eventTypes.DATA_PRESENT, this.getPresentData());
		}
	}

	/**
	 * Collect coordinate information about every indicator's final point
	 * @returns Array of objects in this format:
	 * [{
	 * 	id: <string>,
	 * 	panelIndex: 0,
	 * 	date: <date>,
	 * 	coords: <double[]>,
	 * 	params: <object>,
	 * 	value: <number>
	 * },...]
	 *
	 */
	getPresentData() {
		const output = [];
		this.panels.forEach((panel, panelIndex) => {
			panel.indicators.forEach(ind => {
				const symbol = ind.params.symbol || this.params.symbol;
				const symbolData = this.data[symbol];
				const out = {};
				if (symbolData && symbolData[ind.params.id]) {
					const data = this.getFirstDataset(symbolData[ind.params.id]); // this.data['205778'].volume
					if (data.length) {
						const last = data[data.length - 1];
						out.id = ind.params.id;
						out.panelIndex = panelIndex;
						for (const seriesId in last) {
							if (
								!/dateIndex|hollow|diff/.test(seriesId) &&
								seriesId !== ind.params.id
							) {
								out[seriesId] = last[seriesId];
							}
						}
						if (last[ind.params.id] || last.close) {
							const scale = panel.yAxis.primaryScale;
							out.value = last[ind.params.id] || last.close;
							out.params = { ...ind.params };
							out.coords = [
								this.params.yAxisSide === 'right' ? panel.size.left + panel.size.width : panel.size.left,
								scale(out.value)
							];
						}
						output.push(out);
					}
				}
			});
		});
		return output;
	}

	/** Wrapping the render in a requestAnimationFrame */
	renderQueue() {
		if (window.requestAnimationFrame) {
			window.cancelAnimationFrame(this.renderAnimFrame);

			this.renderAnimFrame = window.requestAnimationFrame(() => this.render());
		} else {
			this.render();
		}
	}

	/** Call render repeatedly with an animated clip region */
	animate(duration, onComplete) {
		// cubic easing in/out (http://gizma.com/easing/)
		function easing(t, b, c, d) {
			t /= d / 2;
			if (t < 1) {
				return (c / 2) * t * t * t + b;
			}
			t -= 2;
			return (c / 2) * (t * t * t + 2) + b;
		}

		const renderFrame = () => {
			const diffTime = currTime - startTime;
			if (diffTime < duration) {
				this.render({
					clipWidth: easing(diffTime, 0, 1, duration)
				});

				currTime = new Date().getTime();

				if (window.requestAnimationFrame) {
					window.requestAnimationFrame(renderFrame);
				} else {
					renderFrame();
				}
			} else {
				// prevent errors when chart is destroyed before animation is complete
				try {
					this.render();
					this.state.isAnimating = false;

					// restore zoom/pan
					this.setZoomEnabled(zoomEnabled);

					if (onComplete)	onComplete();
				} catch (e) {
					console.warn('Animation cancelled');
				}
			}
		};

		const startTime = new Date().getTime();
		let currTime = startTime;
		const zoomEnabled = this.params.zoomEnabled;

		// set duration of animation
		duration = duration || this.params.animationDuration;

		this.state.isAnimating = true;

		// disable zoom/pan
		this.setZoomEnabled(false);

		// kick off animation
		window.requestAnimationFrame(renderFrame);
	}

	/** Deletes all panels from panels collection */
	removePanels() {
		this.panels.slice().reverse().forEach(panel => this.removePanel(panel));
	}

	/** Clears all panel canvases */
	clearPanels() {
		this.panels.forEach(panel => panel.clearPanel());
	}

	/**
	 * @method concealLegends
	 */
	concealLegends() {
		this.panels.forEach(panel => {
			panel.concealLegend();
		});
	}

	/**
	 * @method revealLegends
	 */
	revealLegends() {
		this.panels.forEach(panel => {
			panel.revealLegend();
		});
	}

	/**
	 * save all customized chart params to a portable JSON string
	 * @method save
	 * @return {string}
	 */
	async save(noStore = true, name = 'Saved Chart', args = {}) {
		const save = {
			params: { ...this.params },
			panels: [],
			metaData: {}
		};

		// Get JSON for each panels
		this.panels.forEach(panel => {
			const panelJSON = panel.toJSON();
			save.panels.push(panelJSON);
		});

		// Get chart meta data
		save.metaData = { ...this.metaData };

		// Decide if chart should be saved in cloud
		if (!noStore) {
			return this.saveData(save, name, args);
		}
		return Promise.resolve(save);
	}

	/** Make ajax call to ChartApi/Save endpoint with blob of JSON */
	async saveData(save, name = 'Saved Chart', args = {}) {
		const message = window.encodeURIComponent(JSON.stringify(save));
		const errMsg = "Can't complete saveData request";
		name = window.encodeURIComponent(name);
		const qsArgs = [
			`name=${name}`,
			`json=${message}`
		];
		// allow override of existing template/chart (CH-1890)
		if (args.id) qsArgs.push(`id=${args.id}`);

		try {
			const response = await axios.post(
				`${this.params.apiPath}ChartApi/Save`,
				qsArgs.join('&'),
				{
					headers: this._getXHRHeaders('application/x-www-form-urlencoded'),
					withCredentials: this.params.xhrWithCredentials ? true : undefined
				}
			);

			const result = this.parseDebugInfo(response.request.responseText);
			if (result.Id) {
				this.eventEmitter.emit(eventTypes.SAVE_COMPLETE, {
					id: result.Id,
					save,
					result,
					args
				});
				return {
					id: result.Id,
					save,
					result,
					args
				};
			}
			throw new Error(
				errMsg
			);
		} catch (error) {
			throw new Error(
				error.message ||
				errMsg
			);
		}
	}

	/**
	 * Makes an XHR call to ChartApi/SaveTemplate endpoint with blob of JSON
	 */
	async saveTemplateData(save, args = {}) {
		const name = window.encodeURIComponent(args.name || 'Saved Template');
		const message = window.encodeURIComponent(JSON.stringify(save));
		const errMsg = "Can't complete saveTemplateData request";
		const qsArgs = [
			`name=${name}`,
			`json=${message}`,
			`isDefault=${String(typeof args.isDefault === 'boolean' ? args.isDefault : false)}`
		];
		// allow override of existing template/chart (CH-1890)
		if (args.id) qsArgs.push(`id=${args.id}`);

		try {
			const response = await axios.post(
				`${this.params.apiPath}ChartApi/SaveTemplate`,
				qsArgs.join('&'),
				{
					headers: this._getXHRHeaders('application/x-www-form-urlencoded'),
					withCredentials: this.params.xhrWithCredentials ? true : undefined
				}
			);

			const result = this.parseDebugInfo(response.request.responseText);
			if (result.Id) {
				this.eventEmitter.emit(eventTypes.TEMPLATE_SAVE_COMPLETE, {
					id: result.Id,
					save,
					result,
					args
				});
				return {
					id: result.Id,
					save,
					result,
					args
				};
			}
			throw new Error(
				errMsg
			);
		} catch (error) {
			throw new Error(
				error.message ||
				errMsg
			);
		}
	}

	/**
	 * save chart template
	 */
	async saveTemplate(noStore = true, name = 'Saved Template', args = {}) {
		const saveData = await this.save();
		const skipIndicators = ['horizontalannotation'];
		let panelStyle = {};

		// only save selected params
		const templateParams = {
			dataInterval: saveData.params.dataInterval,
			dataPeriod: saveData.params.dataPeriod,
			symbolCompare: saveData.params.symbolCompare,
			panelHeightUpper: saveData.params.panelHeightUpper,
			panelHeightLower: saveData.params.panelHeightLower,
			panelXAxis: saveData.params.panelXAxis,
			yAxisScale: saveData.params.yAxisScale,
			extendedHours: saveData.params.extendedHours,
			showComponentSeriesFlags: saveData.params.showComponentSeriesFlags,
			showXAxisFlag: saveData.params.showXAxisFlag,
			hiddenLowers: saveData.params.hiddenLowers
		};

		// save either date range or days
		if (saveData.params.dateStart && saveData.params.dateStop) {
			templateParams.dateStart = new Date(saveData.params.dateStart);
			templateParams.dateStop = new Date(saveData.params.dateStop);
		} else if (saveData.params.days) {
			templateParams.days = saveData.params.days;
		}

		saveData.params = {};

		// meta data
		saveData.metaData = { ...{}, ...this.metaData };

		// templateParams
		Object.keys(templateParams).forEach(paramKey => {
			const paramValue = templateParams[paramKey];
			saveData.params[paramKey] = paramValue;
		});

		// panels
		saveData.panels.forEach(panel => {
			if (args.panelStyles === 'all') {
				// save all panel styles (useful if chart was not relying on CSS for styling)
				panelStyle = { ...{}, ...panel.params.style };
			} else {
				// only save selected panel styles
				const { padding } = panel.params.style.yAxis;
				panelStyle = {
					yAxis: { ...padding }
				};
			}

			panel.params.style = panelStyle;

			// set (only) primary symbol params to -1 so they will be dynamic when loadTemplate is called.
			// non-primary symbols will be retained as comparisons. (see NXC-566)
			for (let x = panel.indicators.length - 1; x >= 0; x--) {
				// don't save certain indicators
				if (skipIndicators.includes(panel.indicators[x].id)) {
					panel.indicators.splice(x, 1);
					continue;
				}

				if (panel.indicators[x].symbol === this.params.symbol) {
					panel.indicators[x].symbol = -1;
				}
			}

			// don't save tools on templates
			delete panel.tools;
		});

		// Decide if template should be saved in cloud
		if (!noStore && typeof name === 'string') {
			args.name = name;
			return this.saveTemplateData(saveData, args);
		}
		return Promise.resolve(saveData);
	}

	/**
	 * Load a saved chart using json data retrieved from the ChartApi/Load endpoint
	 * @param {string} id - The chart ID to be retrieved
	 * @param {function} callback - Function to call after chart has loaded
	 * @param {boolean} loadChart - Should the saved chart get loaded
	 * @returns {object} Chart params for retrieved chart
	 */
	async loadSaved(id, callback, loadChart = true) {
		const errMsg = 'There was an error retrieving the saved chart';
		this.exchangeDates = [];

		try {
			const response = await axios.post(
				`${this.params.apiPath}chartapi/load/${id}`,
				{},
				{
					headers: this._getXHRHeaders(),
					withCredentials: this.params.xhrWithCredentials ? true : undefined
				}
			);
			let result = this.parseDebugInfo(response.request.responseText);

			// Add check to avoid unhandled rejection errors during testing
			if (typeof result === 'string') {
				// Clean up response and convert to object
				result = JSON.parse(
					result
						.replace(/&quot;/g, '"')
						.replace(/&#174;/g, '®')
						.replace(/\t/g, '    ')
				);
			}

			// Load chart
			if (loadChart) this.load(result, callback);

			return result;
		} catch (error) {
			throw new Error(
				error.message ||
				errMsg
			);
		}
	}

	/**
	 * Return a list of saved charts/templates/images from the chartapi/list endpoint
	 * @param {string} saveType - The chart type to retrieve (Modcharts/Modcharts-Template/Modcharts-Image)
	 * @returns {object[]} Array of saved items
	 */
	async listSaved(saveType = null) {
		const errMsg = 'There was an error retrieving the list of saved items';
		try {
			const response = await axios.post(
				`${this.params.apiPath}chartapi/list/`,
				{},
				{
					headers: this._getXHRHeaders(),
					withCredentials: this.params.xhrWithCredentials ? true : undefined
				}
			);
			//const result = this.parseDebugInfo(response.request.reponseText);
			const result = response.request;

			if (saveType && result.filter) {
				return result.filter(row => row.Category === saveType);
			}
			return result;

		} catch (error) {
			throw new Error(
				error.message ||
				errMsg
			);
		}
	}

	/**
	 * Return the current default template Id if one exists
	 * @returns {string} template Id
	 */
	async loadDefaultTemplateId() {
		try {
			const response = await axios.post(
				`${this.params.apiPath}chartapi/loaddefaulttemplateid`,
				{},
				{
					headers: this._getXHRHeaders(),
					withCredentials: this.params.xhrWithCredentials ? true : undefined
				}
			);
			return this.parseDebugInfo(response.request.responseText);
		} catch (error) {
			throw new Error(
				error.message ||
				'There was an error retrieving the default template id'
			);
		}
	}

	/**
	 * Return the current default template Id if one exists
	 * @param {string} id - The saved id
	 * @param {string} category - Modcharts / Modcharts-Template / Modcharts-Image
	 * @returns {string} status
	 */
	async deleteSaved(id = null, category = 'Modcharts') {
		if (id) {
			try {
				const response = await axios.post(
					`${this.params.apiPath}chartapi/delete?id=${id}&category=${category}`,
					{},
					{
						headers: this._getXHRHeaders(),
						withCredentials: this.params.xhrWithCredentials ? true : undefined
					}
				);
				return this.parseDebugInfo(response.request.responseText);
			} catch (error) {
				throw new Error(
					error.message ||
					'There was an error deleting the saved item'
				);
			}
		}
	}

	/**
	 * Deletes the current default template Id if one exists
	 * @returns {string} status
	 */
	async deleteDefaultTemplateId() {
		try {
			const response = await axios.post(
				`${this.params.apiPath}chartapi/deletedefaulttemplateid`,
				{},
				{
					headers: this._getXHRHeaders(),
					withCredentials: this.params.xhrWithCredentials ? true : undefined
				}
			);
			return this.parseDebugInfo(response.request.responseText);
		} catch (error) {
			throw new Error(
				error.message ||
				'There was an error deleting the default template id'
			);
		}
	}

	/**
	 * Return the current inception date
	 * @returns {object} The first trading date available for symbol
	 */
	async loadInceptionDate(xid = this.params.symbol) {
		try {
			const response = await axios.post(
				`${this.params.apiPath}chartapi/inceptiondate?symbol=${xid}`,
				{},
				{
					headers: this._getXHRHeaders(),
					withCredentials: this.params.xhrWithCredentials ? true : undefined
				}
			);
			const result = this.parseDebugInfo(response.request.responseText);
			const msDate = result.InceptionDate > 0 ? result.InceptionDate : null;
			const date = msDate ? this.msToJsDate(msDate) : null;
			return {
				symbol: result.Symbol,
				msDate,
				date
			};
		} catch (error) {
			throw new Error(
				error.message ||
				'There was an error retrieving the inception date'
			);
		}
	}

	/**
	 * return appropriate XHR request headers
	 * @return {object}
	 */
	_getXHRHeaders(contentType = 'application/json', authToken = null) {
		const bearerToken = authToken || this.state.authToken;
		const headers = {
			'Content-Type': contentType
		};
		if (bearerToken) {
			headers.Authorization = `Bearer ${bearerToken}`;
		} else if (this.state.accessToken) {
			headers['X-MOD-ACCESS-TOKEN'] = this.state.accessToken;
		}

		// Add optional headers from this.params.xhrRequestHeaders
		this.params.xhrRequestHeaders.forEach(row => {
			headers[row.name] = row.value;
		});

		return headers;
	}

	/**
	 * load a saved template using json data retrieved from the ChartApi/Template endpoint
	 */
	async template(id, args = {}) {
		await axios.post(
			`${this.params.apiPath}chartapi/template/${id}`,
			{},
			{
				headers: this._getXHRHeaders(),
				withCredentials: this.params.xhrWithCredentials ? true : undefined
			})
			.then(res => {
				const result = this.parseDebugInfo(res.request.responseText);

				if (!result || (!result.params && !result.ChartSettings)) {
					// response did not have expected values
					if (!this.eventEmitter.listeners('TEMPLATE_LOAD_ERROR').length) {
						this.showMessageChartNotAvailable({
							description: 'Invalid template response'
						});
					}
					this.eventEmitter.emit(eventTypes.TEMPLATE_LOAD_ERROR, { id, args, result });
				} else {
					this.applyTemplate(result, args);
					this.loadData();
				}
				this.eventEmitter.emit(eventTypes.TEMPLATE_LOAD, { id, result, args });
				return result;
			})
			.catch(error => {
				// catch any other error with XHR processing
				this.warn(`An error occurred.\n\n${error.message}`);
				if (!this.eventEmitter.listeners('TEMPLATE_LOAD_ERROR').length) {
					this.showMessageChartNotAvailable({
						description: 'Unknown error loading template',
						error
					});
				}
				this.eventEmitter.emit(eventTypes.TEMPLATE_LOAD_ERROR);
			});
	}

	/** apply template params against current chart prior to loading new data */
	applyTemplate(data = {}, args = {}) {
		// Recalculate present scale domain because saved params are being loaded
		this.state.updateXAxisPresentScaleDomain = true;

		let eventParams;

		// merge in the new params
		if (data.params && this.params) {
			merge(this.params, data.params);
		}

		// AC-91 remove comparisons that match primary symbol
		if (this.params.symbolCompare) {
			this.params.symbolCompare =
				this.params.symbolCompare.filter(symbol => symbol !== this.params.symbol);
		}

		// convert dateStart/Stop back to date object
		if (data.params.dateStart && data.params.dateStop) {
			this.params.dateStart = new Date(data.params.dateStart);
			this.params.dateStop = new Date(data.params.dateStop);

			// Clear the initial transform since a custom date range has been set and it will need to be redefined
			this.state.initialTransform = null;
		} else if (data.params.days) {
			// CW-387
			this.params.dateStart = null;
			this.params.dateStop = null;
		}

		// prepare for new panels
		this.removePanels();

		data.panels.forEach((savedPanel, savedPanelIdx) => {
			// if panelParams was passed in, merge it into new panel params.
			const panelParams = {
				...{},
				...savedPanel.params,
				...(args.panelParams || {})
			};
			const panel = this.addPanel(panelParams);

			// restore indicators
			savedPanel.indicators.forEach((row, idx) => {
				// symbols of -1 were previously the primary symbol - replace with the current primary symbol
				if (row.symbol === -1) {
					row.symbol = this.params.symbol;
				}

				// AC-91 don't add comparison price indicators that match primary symbol
				if (
					savedPanelIdx === 0 &&
					idx > 0 &&
					row.symbol === this.params.symbol
				) {
					return true;
				}

				// fix grandfathered templates with erroneous prototypeJS arrays
				if (typeof row.inputs === 'string') {
					row.inputs = JSON.parse(row.inputs);
				}
				if (typeof row.extraInputs === 'string') {
					row.extraInputs = JSON.parse(row.extraInputs);
				}

				panel.addIndicator(row.id, row);
			});

			// restore events
			for (let i = 0; i < (savedPanel.events || []).length; i++) {
				eventParams = savedPanel.events[i].params;
				panel.addEvent(
					savedPanel.events[i].id || eventParams.id,
					eventParams || {}
				);
			}
		});

		// scale
		if (this.panels.length && this.params.yAxisScale) {
			this.panels[0].setScale(this.params.yAxisScale);
		}

		// restore metadata
		if (data.metaData) {
			// fix grandfathered templates with erroneous prototypeJS arrays
			if (typeof data.metaData === 'string') {
				data.metaData = JSON.parse(data.metaData);
			}

			this.metaData = { ...{}, ...data.metaData };
		}

		this.eventEmitter.emit(eventTypes.TEMPLATE_APPLY, {
			data,
			args
		});
	}

	/**
	 * load a chart via JS or a saved JSON string
	 * @param {string|object} newParams
	 * @return {Modcharts}
	 */
	load(newParams, callback, args) {
		return this.chartLoader.load(newParams, callback, args);
	}

	/**
	 * Virtual Method for charts that accept tools
	 * @method getToolsFromJSON
	 */
	getToolsFromJSON() {
		return [];
	}

	/**
	 * Returns the number of trading days within calendar date range based on the exchangeDates ruler
	 * @param {Date} dateStart - Start date
	 * @param {Date} dateStop - Stop date
	 * @returns {number} The number of trading days
	 */
	getTradingDays(dateStart, dateStop) {
		const idxStart = this.closestExchangeIndex(dateStart);
		const idxStop = this.closestExchangeIndex(dateStop);
		let idx = idxStart;
		let days = this.state.isIntraday ? 0 : 1;
		let currDay = new Date(this.exchangeDates[idx]).getDate();

		while (idx <= idxStop) {
			const thisDay = new Date(this.exchangeDates[idx++]).getDate();

			if (currDay !== thisDay) {
				days++;
				currDay = thisDay;
			}
		}

		return Math.max(days, 1);
	}

	/** Returns the number of days in the year to date */
	getYTDDays() {
		const date = new Date();
		const jan = new Date(date.getFullYear(), 0, 1);

		return Math.ceil((date - jan) / 1000 / 60 / 60 / 24);
	}

	/**
	 * begin polling - pass pollMin of 0 to turn off poll
	 * @method pollData
	 */
	pollData(pollMin) {
		if (typeof pollMin === 'number') {
			this.params.poll = pollMin;
		}

		// clear any still-active setTimeouts
		if (this.state.pollTimeout) {
			window.clearTimeout(this.state.pollTimeout);
			this.state.pollTimeout = null;
		}

		// fire off new poll
		if (this.params.poll > 0) {
			this.state.pollTimeout = window.setTimeout(() => {
				// don't poll while suppressed - try again after next poll interval
				if (this.isPollStreamSuppressed()) {
					// short delay before retry
					window.setTimeout(() => {
						return this.pollData();
					}, 3000);
				} else {
					// load new data
					this.loadData(() => {
						// resume polling after data load completes
						this.pollData();

						// if an intraday chart is left up overnight we could potentially blow away the previous
						// days' data with the first poll on the new day.  so add a backfill check here.
						if (this.params.days < this.limitIntradayTrading) {
							this.backfillData();
						}
					},
					{ silent: true }
					);
				}
			}, this.params.poll * 60 * 1000);
		}
	}

	/**
	 * detect whether to suppress polling/streaming at this moment based on current chart state
	 */
	isPollStreamSuppressed(isStream) {
		// don't poll for data while in the middle of loading data, resizing panels, or drawing tools
		// also wait 1000ms after last zoom to make sure mousewheel is done firing

		if (this.state.zooming) {
			return true;
		} else if (this.dataXHR) {
			return true;
		} else if (
			typeof this.state.zoomDate === 'number' &&
			new Date() - this.state.zoomDate < 1000
		) {
			return true;
		} else if (this.state.toolmode && !isStream) {
			return true;
		} else if (this.state.resizePanel) {
			return true;
		} else if (this.state.isAnimating) {
			return true;
		}

		return false;
	}

	/**
	 * Make an XHR request for chart data and process it into a standardized data structure.
	 * typically this will be a request to <api domain>/ChartApi/Series but it can support other calls as well
	 * @param {Function} callback - Callback function passed to _processChartApiResponse
	 * @param {object} args - Arguments passed to _processChartApiResponse
	 */
	loadData(callback, args = {}) {

		// prevent infinite invalid token requests (CH-2872)
		if (this.state.invalidTokenCount > this.params.invalidTokenLimit) {
			return this.warn('Invalid authorization token count limit reached.  Please refresh the page.');
		}

		const path = this.params.chartAPI;
		const isYTD = this.params.days === -1;
		const authToken = this._getAuthToken();

		// Reset initial transform if loading new chart
		if (args && args.loadSaved) this.state.initialTransform = null;

		// Cancel ongoing XHR
		if (this.xhrCancelSource) this.xhrCancelSource.cancel();
		this.dataXHR = null;

		// Enforce linear scale on normalized charts
		this._enforceLinearScale();

		// Show loading graphic and reset notable vars
		if (!args.silent) {
			this.eventEmitter.emit(eventTypes.DATA_LOAD_START);
			this.loadingStart();

			if (!args.prepend) this._resetNormalizeDate();
		}

		// set days for YTD charts
		if (isYTD) this.params.days = this.getYTDDays();

		this.state.isIntraday = this._getIntradayStatus();

		// Build XHR
		this.dataXHR = this._buildXHR(authToken);

		// Send request to API
		this._sendXHR(
			this._setPathDebugParams(path),
			this._getChartAPIInputs(args),
			callback,
			args
		);

		if (isYTD) this.params.days = -1;

		return true;
	}

	/** Resets normalizeDate params (normalizeDate, normalizeValue, normalizeValues, exchangeDates) */
	_resetNormalizeDate() {
		this.params.normalizeDate = null;
		this.params.normalizeValue = null;
		this.params.normalizeValues = null;
		this.exchangeDates = [];
	}

	/**
	 * Determines if data should be intraday based on dateStart, dateStop, days and dataPrimary and limitIntradayMs
	 * @returns {boolean}
	 */
	_getIntradayStatus() {

		// CH-3204 reduce limitIntradayTrading to 21 days for non-FX charts
		// this is only possible after the first data load because we need to know the issueType
		if (
			this.dataPrimary?.length &&
			this.params.symbol &&
			this.xref[this.params.symbol]
		) {
			const issueType = this.xref[this.params.symbol].issueType;
			this.limitIntradayTrading = (issueType === 'FX') ? 31 : 21;
		}

		const limitIntradayTradingMs = this.limitIntradayTrading * 1000 * 60 * 60 * 24;
		const limitIntradayCalendarMs = this.limitIntradayCalendar * 1000 * 60 * 60 * 24;

		let isIntraday = false;
		if (this.params.dateStart && this.params.dateStop) {
			isIntraday = /Hour|Minute/.test(this.params.dataPeriod);
		} else if (this.params.days) {
			isIntraday =
				this.params.days * 1000 * 60 * 60 * 24 <= limitIntradayTradingMs &&
				/hour|minute/i.test(this.params.dataPeriod);
		} else if (this.dataPrimary && this.dataPrimary.length) {
			isIntraday =
				new Date() - this.dataPrimary[0].date <= limitIntradayCalendarMs &&
				this.dataPrimary[this.dataPrimary.length - 1].date -
					this.dataPrimary[0].date <=
					limitIntradayCalendarMs;
		}
		return isIntraday;
	}

	/** Ensures that primary panel is linear scale when the primary panel is normalized */
	_enforceLinearScale() {
		if (this.params.yAxisScale === 'log' &&
			this.panels.length &&
			this.panels[0].isNormalized) {
			this.panels[0].setScale('linear');
		}
	}

	/**
	 * Gets auth token based on state and params.apiAuthMode
	 * @returns {String} Auth Token
	 */
	_getAuthToken() {
		const shouldReturnToken =
			this.state.authToken &&
			!this.state.accessToken &&
			this.params.apiAuthMode !== 'querystring';

		return shouldReturnToken
			? this.state.authToken
			: null;
	}

	/**
	 * Modifies the API path with any debug parameters
	 * @method _setPathDebugParams
	 * @param {String} path
	 * @param {string} [search] - optional search parameter
	 */
	_setPathDebugParams(path, search) {
		const debugInfo = this.getParameterByName('..showdebuginfo..', search);
		const noCache = this.getParameterByName('..nocache..', search);
		const kvp = [];

		if (debugInfo && debugInfo.length) {
			kvp.push(`..showdebuginfo..=${debugInfo}`);
		}

		if (noCache && noCache.length) {
			kvp.push(`..nocache..=${noCache}`);
		}

		if (this.state.authToken && this.params.apiAuthMode === 'querystring') {
			kvp.push(`access_token=${this.state.authToken}`);
		}

		if (kvp.length) {
			const qsPrefix = /\?/.test(path) ? '&' : '?';
			path += qsPrefix + kvp.join('&');
		}
		return path;
	}

	/**
	 * Build the XHR object to make an API Request for new data
	 * @param {String} authToken - API authorization token
	 */
	_buildXHR(authToken) {
		// Initialize Axios cancel source for request cancellation; each request gets its own source
		this.xhrCancelSource = axios.CancelToken.source();

		return axios.create(
			{
				cancelToken: this.xhrCancelSource.token,
				headers: this._getXHRHeaders(this.params.apiContentType, authToken),
				withCredentials: this.params.xhrWithCredentials ? true : undefined,
				method: 'post'
			});
	}


	/**
 * Sends XHR with appropriate inputs
 * @param {String} path - URL for request
 * @param {Object} inputs - Data to be sent in request
 * @param {Function} callback - Optional callback function once request completes
 * @param {*} args - Collection of optional arguments
 */
	_sendXHR(path, inputs, callback, args = {}) {

		// Process a mock response for testing.  If mockResponse is passed in, skip the XHR request altogether.
		if (args.mockResponse) {
			this._processChartApiResponse(args.mockResponse, null, callback, args);
			this.dataXHR = null;
			return;
		}

		// Post the request
		this.dataXHR.request({
			url: path,
			data: inputs
		})
			.then(res => {
				// Handle successful response
				const result = this.parseDebugInfo(res.request.responseText);
				this._processChartApiResponse(result, null, callback, args);
			})
			.catch(error => {
				let errObject;

				if (axios.isCancel(error)) {
					// Don't throw an error on a purposeful request cancellation. Do nothing for now.
					this.dataXHR = null;
				} else {
					if (error.response) {
						// Response was received with a non 2XX status code
						errObject = {
							responseText: this.parseDebugInfo(error.request.responseText),
							status: error.request.status,
							statusText: error.request.statusText,
							responseType: error.request.responseType
						};
					} else if (error.request) {
						// The request was made but no response was received
						errObject = new Error('No response to request');
						errObject.name = 'XHR Error';
					} else {
						// Error when setting up the request
						console.error(error);
						errObject = new Error(`Incorrect request configuration: ${error}`);
						errObject.name = 'XHR Error';
					}
					this._processChartApiResponse({}, errObject, callback, args);
				}
			})
			.finally(() => {
				// The XHR object needs to be cleared as some functions will assume a request
				// is still ongoing if the request isn't cleared
				this.dataXHR = null;
			});
	}

	/**
	 * Split off the debuginfo part of response, add it to DOM so it fires the debug popup,
	 * then return just the json part of the response
	 * @param {String} text - Raw text of response, which may contain debug info
	 * @returns {JSON} JSON part of the response, without the debug info
	 */
	parseDebugInfo(text) {
		const rows = (text || '').split('\n');
		let json = {};

		if (rows.length && rows[0] === '{') {
			console.warn(JSON.stringify(rows));
			return;
		}

		try {
			// Remove JSON from response and save it for later
			json = JSON.parse(rows.shift());
		} catch (e) {
			console.warn(e);
		}

		// Only add debug code if it exists
		if (rows.length) {
			// Get debug container from DOM
			let debugContainer = document.getElementById('debuginfo-container');

			// If debug container doesn't exist, create it
			if (!debugContainer) {
				debugContainer = document.createElement('div');
				debugContainer.setAttribute('id', 'debuginfo-container');
				document.body.appendChild(debugContainer);
			}

			// See if first row contains opening/closing script tags for debug.js file
			const hasDebugScriptTag =
				/^<script/.test(rows[0]) &&
				/^.*<\/script>$/m.test(rows[0]);

			if (hasDebugScriptTag) {
				// Add debug script tag to document
				let debugScriptTag = rows.shift();

				// TODO: Is this still needed?
				// Switch to different debug.js location if we're a Node.js site
				if (
					typeof window.Debug !== 'undefined' &&
					/index\.html/.test(window.Debug.url || '')
				) {
					debugScriptTag = debugScriptTag.replace(
						'/includes/Jslib/WSDOM/Debug/debug.js',
						'/includes/showdebuginfo/debug.js'
					);
				}
				debugContainer.innerHTML = debugScriptTag;
			}

			// Find next script tag which contains our debug statements
			const indexNextScriptTag = rows.findIndex(row => /^<script/.test(row));

			let resDebugScript;

			// If next script tag found, get everything inside script tags and add it to an element
			if (indexNextScriptTag > -1) {
				resDebugScript = rows.slice(indexNextScriptTag + 1, -1).join('\n');
			} else {
				// Add all rows to new script tag
				resDebugScript = rows.join('\n');
			}

			// Create new element to store debug script from response, add script, add element
			const resDebugTag = document.createElement('script');
			resDebugTag.innerHTML = resDebugScript;

			try {
				// Add response debug script to DOM
				debugContainer.appendChild(resDebugTag);
			} catch (e) {
				this.warn(e.message);
			}
		}

		return json;
	}

	/**
	 * Similar to loadData except it doesn't do an ajax request as it already has data.
	 * Initialize some variables and kick off the _processCustomResponse call.
	 * @param {Function} callback - Callback function passed to _processCustomResponse
	 * @param {object} args - Arguments passed to _processCustomResponse
	 */
	loadCustomData(callback, args = {}) {
		// Reset initial transform if loading new chart
		if (args && args.loadSaved) this.state.initialTransform = null;

		// show loading graphic and reset notable vars

		this.eventEmitter.emit(eventTypes.DATA_LOAD_START);

		this.loadingStart();

		this.exchangeDates = [];

		// determine state.isIntraday
		this.state.isIntraday = false; // currently hardcoded to false to prevent odd rendering issues with custom intraday data.

		return this._processCustomResponse(
			this.params.customData,
			null,
			callback,
			args
		);
	}

	/**
	 * Find the correct start/stop domain indices for the current intraday dataset.
	 * Here are a few scenarios:
	 * Scenario #1: 1-day/15-minute intraday chart, loaded at noon.
	 * First datapoint in the series is at 9:45, last datapoint is at 11:45.
	 * However the eventual goal is to present an XAxis from 9:30 to 16:00.
	 * _getIntradaySessionIndex is called twice, once to obtain the 9:30 domain index and once for the 16:00 index.
	 * It looks at the dateIn to determine the current day, and sets the time portion to midnight.
	 * Then offsets the minutes using a session value obtained from the timeService collection.
	 * For instance on a typical Monday for NSQ, the session will begin at 570 minutes past midnight and end at 960 minutes.
	 * Scenario #2: 5-day/5-minute intraday chart, loaded at noon on a Tuesday.
	 * First datapoint in the data series is at 9:35 last Wednesday.  Last point is 11:55 on this Tuesday.
	 * In this case we want the XAxis to begin at 9:30 last Wednesday and end at 16:00 on this Tuesday.
	 * @param {Date} dateIn A date object from a given point in the data collection
	 * @param {Number} offsetMinutes Session start (or end) time, offset from midnight (in minutes)
	 * @return {Number} The domain index that corresponds to the session offset for the dateIn input
	 * @private
	 */
	_getIntradaySessionIndex(dateIn, offsetMinutes) {
		let date = new Date(dateIn);
		const exchangeOffset = this.getExchangeOffsetInput();

		// adjust for correct session day (CW-653)
		if (exchangeOffset > 0 && this.xref[this.params.symbol]) {
			const utcOffset = this.xref[this.params.symbol].utcOffset;
			date = new Date(
				date.setMinutes(date.getMinutes() - utcOffset + exchangeOffset)
			);
		}

		// snap to the offsetMinutes passed (session open or session close)
		date.setHours(0, offsetMinutes, 0, 0);

		return this.closestExchangeIndex(date);
	}

	/**
	 * Calculates padding for domain given the current domain bounds and the padding value in pixels.
	 * This is useful in cases when the scale domain hasn't been set yet.
	 * @param {Number} min - Lower bound for current domain
	 * @param {Number} max - Upper bound for current domain
	 * @param {Number} padding - Padding value in pixels to add to the domain
	 * @returns {Number} The padding value in domain units
	 */
	_calcXAxisDomainPadding(min, max, padding) {
		return (max - min) * (padding / this.panels[0].size.width);
	}

	/**
	 * Determines the proper new domain to use (eg. where the XAxis begins and ends).
	 * By default the domain will stretch to the full dataset width.
	 * @param {*} primary - The data set to be used for calculating the domain
	 * @param {*} panelDomain - The existing domain to be used instead of calculating a new one
	 * @param {*} applyAdjustments - Flag to control if domain adjustments should be applied
	 * @returns
	 */
	getDefaultDomain(primary = this.dataPrimary, panelDomain = this.panels[0].xAxis.primaryScale.domain(), applyAdjustments = true) {
		let left = 0;
		let right = 0;
		const panel = this.panels[0];
		// ichimoku is different because some of its datasets plot into the future.
		const hasIchimoku =	panel.indicators.filter(i => i.params.id === 'ichimokucloud').length > 0;
		// make sure the domain is valid, and not just undefined or [0,0] (which is the reset state for a domain).
		const hasNonZeroDomain =
			panelDomain &&
			panelDomain[1] - panelDomain[0] > 0;
		const isIntraday = this.state.isIntraday && this.state.typicalSessions;
		// try to determine if the chart has been panned by the user.  if so, keep the domain as-is,
		// since you don't want it resetting on backfill requests.
		const hasPanned = hasNonZeroDomain &&
			this.dataPrimary &&
			this.dataPrimary.length > 0 &&
			(
				this.params.endOffsetDays ||
				(
					!hasIchimoku &&
					Math.abs(panelDomain[1] - this.closestExchangeIndex(this.dataPrimary[this.dataPrimary.length - 1].date)) > 2
				)
			);
		// (saved charts) if we found a saved dateStart and dateStop with no domain, try to use it
		// else use existing domain if neq [0,0]
		// else default to full range of loaded dataset
		if (
			panelDomain && // This is needed to make sure panelDomain was actually defined, not just non-zero
			!hasNonZeroDomain &&
			this.params.dateStart &&
			this.params.dateStop
		) {
			left = this.closestExchangeIndex(new Date(this.params.dateStart));
			right = this.closestExchangeIndex(new Date(this.params.dateStop));

			// same left/right domain
			if (right === left) {
				// valid domain (ex: [45, 45]), but the stop must at least be +1 start
				if (
					left !== 0 &&
					right !== 0 &&
					left < this.exchangeDates.length - 1
				) {
					right = left + 1;
				} else {
					// domain wasn't valid or [0,0]
					// fall back to full dataset range instead
					left = this.closestExchangeIndex(primary[0].date);
					right = this.closestExchangeIndex(
						primary[primary.length - 1].date
					);
				}
			}
		} else if (hasNonZeroDomain) {
			// domain is already set, so continue using that.
			left = Math.max(0, panelDomain[0]);
			right = Math.min(this.exchangeDates.length - 1, panelDomain[1]);
		} else if (isIntraday) {
			// intraday domain is not set, so calculate it.
			// begin at the session start time (eg 9:30) and end at the session stop time (eg 16:00)
			// if params.returnPreviousPoint is true, use the second index instead of the first.  if the first is used, it will choose the wrong day, since that will be the session end point of the previous day (eg. 4:00pm)
			const leftIndex = this.params.returnPreviousPoint && primary.length > 1 ? 1 : 0;
			left = this._getIntradaySessionIndex(
				primary[leftIndex].date,
				this.state.typicalSessions.min
			);
			right = this._getIntradaySessionIndex(
				primary[primary.length - 1].date,
				this.state.typicalSessions.max
			);
		} else {
			// interday domain is not set, so calculate it.
			// cinch x-axis to the available data
			// if params.returnPreviousPoint is true, ignore the first datapoint and use the second as the start point.  this prevents a gap on the left, more noticeable with bar charts
			const startIndex =
				this.params.returnPreviousPoint &&
				primary.length > 1 &&
				!this.params.customData &&
				!panel.isNormalized
					? 1 : 0;
			left = this.closestExchangeIndex(primary[startIndex].date);
			right = this.closestExchangeIndex(
				primary[primary.length - 1].date
			);
		}

		// Adjust domain given certain criteria
		if (applyAdjustments) {
			// ichimoku special case: adjust right-hand domain edge since this indicator plots into the future.
			// and don't do this if we have already panned around
			if (hasIchimoku && !hasPanned) {
				// get ichimoku data
				const ichimokuData = this.getDataByType(
					this.params.symbol,
					'ichimokucloud'
				);

				if (ichimokuData.length > 0 && ichimokuData[0].length) {
					// find right edge of data
					const finalDate =
								ichimokuData[0][ichimokuData[0].length - 1].date;
					let finalIndex = this.closestExchangeIndex(finalDate);

					// if intraday, don't shrink earlier than the closing date of the current trading day. (CW-2408)
					if (this.state.isIntraday && this.state.typicalSessions) {
						const dateEnd = new Date(
							this.dataPrimary[this.dataPrimary.length - 1].date
						);
						dateEnd.setHours(0, 0, this.state.typicalSessions.max, 0);
						finalIndex = Math.max(
							finalIndex,
							this.closestExchangeIndex(dateEnd)
						);
					}

					right = finalIndex;
				}
			}

			// Domain adjustment for items that need to be calculated BEFORE the normalized point domain adjustment
			if (
				Object.keys(this.xAxisDomainAdjustmentCategories).length &&
				!hasNonZeroDomain &&
				this.params.useXAxisDomainAdjustment
			) {
				// Run the initial domain adjustment and save it to state
				this.state.initialDomainAdjustment = this._getXAxisDomainAdjustment(left, right, [0, 0]);
			}

			// Domain adjustment for items that need to be calculated AFTER the normalized point domain adjustment
			if (
				Object.keys(this.xAxisDomainAdjustmentCategories).length &&
				!hasNonZeroDomain &&
				this.params.useXAxisDomainAdjustment
			) {
				let [leftDist, rightDist] = this._getXAxisDomainAdjustment(left, right, this.state.initialDomainAdjustment, false);

				// We only check the padding on the upper panel for now. We could potentially
				// add padding per panel, but that doesn't seem useful right now.
				const { padding } = this.panels[0].params.style.xAxis;
				if (padding.left && padding.left > 0) {
					leftDist = Math.max(
						leftDist,
						this._calcXAxisDomainPadding(left, right, padding.left)
					);
				}

				if (padding.right && padding.right > 0) {
					rightDist = Math.max(
						rightDist,
						this._calcXAxisDomainPadding(left, right, padding.right)
					);
				}

				left -= leftDist;
				right += rightDist;
			}
		}

		// return the new domain
		return [left, right];
	}

	/**
	 * Categories defined in xAxisDomainAdjustmentCategories can get truncated when plotted exactly
	 * at the horizontal boundry of a panel. This method determines the additional domain
	 * padding needed to ensure the full width of the item can be displayed, BEFORE normalized
	 * point domain adjustment (if that option is turned on).
	 * @param {number} min - Minimum domain value
	 * @param {number} max - Maximum domain value
	 * @param {Boolean} beforeNormalizedPointDomainAdjustment - Whether or not domain adjustments should take place before normalized point domain adjustment
	 * @returns {number[]} The appropriate amount of XAxis domain padding (left and right) needed for the adjustment categories
	 */
	_getXAxisDomainAdjustment(min = 0, max = 0, [leftDist, rightDist] = [0, 0], beforeNormalizedPointDomainAdjustment = true) {
		if (beforeNormalizedPointDomainAdjustment) {
			let maxBarWidth = 0;
			for (const category in this.xAxisDomainAdjustmentCategories) {
				switch (category) {
					case domainAdjustmentCategories.xAxis.BAR_MARKER:
						// Get marker bar width
						for (const i of this.xAxisDomainAdjustmentCategories[domainAdjustmentCategories.xAxis.BAR_MARKER]) {
							const marker = i.markers[i.params.markerType]
								? i.markers[i.params.markerType]
								: i.markers[priceMarkerTypes.BAR];
							maxBarWidth = Math.max(
								maxBarWidth,
								marker.getBarWidth(i.panel, min, max)
							);
						}

						// Convert half of bar width to domain units (add 5 to show a gap)
						maxBarWidth = this._calcXAxisDomainPadding(min, max, 0.5 * maxBarWidth + 5);

						if (this.state.isIntraday) {
							rightDist = Math.max(rightDist, maxBarWidth);
						} else {
							leftDist = Math.max(leftDist, maxBarWidth);
							rightDist = Math.max(rightDist, maxBarWidth);
						}
						break;
					case domainAdjustmentCategories.xAxis.EVENT:
						// Calculate the proper domain adjustment to account for the icon size showing at the edge of the chart
						leftDist = Math.max(
							leftDist,
							// This will only get the value from the default style
							this._calcXAxisDomainPadding(min, max, this.getStyle('event.xAxis.padding')) * (this.state.isIntraday ? 1 : 0.5)
						);
						rightDist = Math.max(
							rightDist,
							// This will only get the value from the default style
							this._calcXAxisDomainPadding(min, max, this.getStyle('event.xAxis.padding')) * (this.state.isIntraday ? 1 : 0.5)
						);
						break;
					default: break;
				}
			}
		} else {
			let maxShapeLength = 0;
			// If normalized point domain adjustment took place, ignore previously calculated left adjustment distance
			if (this.state.didNormalizedPointDomainAdjustment) leftDist = 0;

			for (const category in this.xAxisDomainAdjustmentCategories) {
				switch (category) { // Leave as switch for now, anticipating that we might need to add other categories in the future
					case domainAdjustmentCategories.xAxis.SHAPE:
						// Check every indicator and get the max length
						for (const i of this.xAxisDomainAdjustmentCategories[domainAdjustmentCategories.xAxis.SHAPE]) {
							maxShapeLength = Math.max(maxShapeLength, i.params.style.shape.length);
						}

						/**
						 * Only apply adjustments to left if:
						 * 1. Chart is interday (because data goes all the way to the left chart edge)
						 * 2. Prepending normalized point (because data goes all the way to the left chart edge, including intraday charts)
						 * */
						if (
							!this.state.isIntraday ||
							this.params.prependNormalizedPoint
						) {
							leftDist = Math.max(
								leftDist,
								this._calcXAxisDomainPadding(min, max, (0.5 * maxShapeLength) + 2) // Add extra pixels to account for border
							);
						}
						rightDist = Math.max(
							rightDist,
							this._calcXAxisDomainPadding(min, max, (0.5 * maxShapeLength) + 1) // Add extra pixel to account for border
						);
						break;
					default:
						break;
				}
			}
		}

		return [leftDist, rightDist];
	}

	/** return first dataset available */
	getFirstDataset(data) {
		for (const x in data) {
			if (Object.prototype.hasOwnProperty.call(data, x)) {
				return data[x];
			}
		}
	}

	/** Return the first non-empty custom dataset available */
	getFirstCustomDataset(data) {
		for (const key in data) {
			if (Object.prototype.hasOwnProperty.call(data, key) && data[key].length > 0) {
				return data[key];
			}
		}
		return null;
	}

	/**
	 * Searches panels array for indicator with UID (eg. "9ffeb4e", "7ac3c3a")
	 * @param {string} uid - Indicator UID to search for
	 * @return {Indicator|undefined} - Indicator that was found (or undefined if not found)
	 */
	getIndicatorByUid(uid) {
		return this.getPanelItemByUid('indicators', uid);
	}

	/**
	 * Searches panels array for indicators with ID (eg. "sma", "bollinger")
	 * @param {string} id - Indicator ID to search for
	 * @return {Indicator[]} - Array of indicators with the specified ID
	 */
	getIndicatorsById(id) {
		const indicators = [];

		this.panels.forEach(panel => {
			panel.indicators.forEach(i => {
				if (id === i.params.id) indicators.push(i);
			});
		});

		return indicators;
	}

	getPanelItemByUid(key, uid) {
		let item;

		for (const panel of this.panels) {
			for (const i of panel[key]) {
				if (uid === i.params.uid) {
					item = i;
					// Found item, break out of loop
					break;
				}
			}
			if (item) break;
		}

		return item;
	}

	/**
	 * Searches panels array for tool with UID (eg. "9ffeb4e", "7ac3c3a")
	 * @param {string} uid - Tool UID to search for
	 * @return {Tool|undefined} - Tool that was found (or undefined if not found)
	 */
	getToolByUid(uid) {
		return this.getPanelItemByUid('tools', uid);
	}

	/**
	 * Searches panels array for event UID (eg. "9ffeb4e", "7ac3c3a")
	 * @param {string} uid - Event UID to search for
	 * @return {Event|undefined} - Event that was found (or undefined if not found)
	 */
	getEventByUid(uid) {
		return this.getPanelItemByUid('events', uid);
	}

	/**
	 * Searches panels array for event datapoint UID (eg. "9ffeb4e", "7ac3c3a")
	 * @param {string} uid - Event datapoint UID to search for
	 * @return {object|undefined} - Event datapoint that was found (or undefined if not found)
	 */
	getEventDatapointByUid(uid) {
		let dataObj;

		for (const panel of this.panels) {
			for (const event of panel.events) {
				if (event.params.dataset) {
					for (const set of event.params.dataset) {
						if (uid === set.uid) {
							dataObj = set;
							break;
						}
					}
				}
				if (dataObj) break;
			}
			if (dataObj) break;
		}

		return dataObj;
	}

	/** convert a normalized (%) figure into its respective actual (requires the starting value) */
	getActualFromNormalized(normalizedValue, normalizedStart) {
		if (!normalizedStart && this.params.normalizeValues) {
			normalizedStart = this.params.normalizeValues.Close;
		}

		if (!normalizedStart) return normalizedValue;

		let val = normalizedStart * (1 + normalizedValue / 100);
		val = Number(val.toFixed(2));

		return val;
	}

	/**
	 * convert an actual figure ($) into its respective normalized value (requires the starting value)
	 */
	getNormalizedFromActual(value, normalizedStart) {
		if (!normalizedStart && this.params.normalizeValues) normalizedStart = this.params.normalizeValues.Close;

		if (!normalizedStart) return value;

		return ((value - normalizedStart) / Math.abs(normalizedStart)) * 100;
	}

	/**
	 * Returns collection of categories that would necessitate a domain adjustment
	 * (eg. if we have a bar-type marker: ohlc, candle, bar, or if events are showing on the chart)
	 * @returns {Object} Collection of categories found which would necessitate a domain adjustment
	 */
	get xAxisDomainAdjustmentCategories() {
		const adjustments = {};

		if (!this.params.useXAxisDomainAdjustment) return adjustments;

		for (const panel of this.panels) {
			for (const indicator of panel.indicators) {
				// Check for bar markers
				if (
					indicator.params.id === 'volume' ||
					(
						/price|custom/.test(indicator.params.id) &&
						/bar|candlestick|hlc/.test(indicator.params.markerType)
					)
				) {
					if (!adjustments[domainAdjustmentCategories.xAxis.BAR_MARKER]) {
						adjustments[domainAdjustmentCategories.xAxis.BAR_MARKER] = [indicator];
					} else {
						adjustments[domainAdjustmentCategories.xAxis.BAR_MARKER].push(indicator);
					}
				}

				// Check for shape marker types
				if (
					indicator.params.markerType === domainAdjustmentCategories.xAxis.LINEWITHSHAPES ||
					indicator.params.markerType === domainAdjustmentCategories.xAxis.SHAPE
				) {
					if (!adjustments[domainAdjustmentCategories.xAxis.SHAPE]) {
						adjustments[domainAdjustmentCategories.xAxis.SHAPE] = [indicator];
					} else {
						adjustments[domainAdjustmentCategories.xAxis.SHAPE].push(indicator);
					}
				}
			}

			// Check for events
			if (panel.events.length > 0) adjustments[domainAdjustmentCategories.xAxis.EVENT] = true;
		}

		return adjustments;
	}

	/**
	 * Searches panels array for uid or id
	 * @param {string} uid - Panel UID to search for
	 * @return {Panel|undefined} - Panel that was found (or undefined if not found)
	 */
	getPanelByUid(uid) {
		let panelMatch;

		for (const panel of this.panels) {
			if (panel.params.uid === uid || panel.params.id === uid) {
				panelMatch = panel;
				break;
			}
		}

		return panelMatch;
	}

	getRandomHexColor() {
		return `#${(0x1000000 + Math.random() * 0xffffff).toString(16).substr(1, 6)}`;
	}

	/**
	 * date index offset of dataset compared to global dataset
	 * @method getDateOffset
	 * @param {array} data
	 * @param {array} dataGlobal
	 * @return {int}
	 */
	getDateOffset(data, dataGlobal) {
		for (let i = 0; i < dataGlobal.length; i++) {
			if (dataGlobal[i] >= data[0]) {
				return i;
			}
		}

		return 0;
	}

	/**
	 * add message to messages div
	 * @method showMessage
	 * @param {string} message
	 */
	showMessage(message) {
		const el = this.rootModchart.node().querySelector('.modcharts-rootmessages');
		if (this.panels.length) {
			el.style.width = `${this.panels[0].size.width}px`;
		}
		const isHTML = /</.test(message);

		// reveal parent element
		this.rootMessages.node().style.display = 'flex';

		const html = isHTML
			? message
			: `<div style="padding:50px;">${message}</div>`;

		el.innerHTML = html;
	}

	/** Clear message DIV */
	clearMessage() {
		const el = this.rootModchart.node().querySelector('.modcharts-rootmessages');

		// conceal parent element
		this.rootMessages.node().style.display = 'none';

		el.innerHTML = '';
	}

	/**
	 * Clear the chart and display a "Chart Not Available" message.
	 * Supports two input types:
	 * - **String**: Displays the provided string as the message.
	 * - **Object**: Uses `message` and `description` properties from the object. Defaults to a translated message if `message` is not provided.
	 * If no valid input is given, defaults to a translated "chart unavailable" message.
	 * @param {string|object} input - The message string or an object with `message` and `description`.
	 */
	showMessageChartNotAvailable(input) {
		let message;
		let description;

		// Check if the input is a string or an object
		if (typeof input === 'string') {
			message = input;
		} else if (typeof input === 'object' && input !== null) {
			message = input.message || translate(this.locale, 'chart.unavailable');
			description = input.description;
		} else {
			// If no argument is passed or if it's an invalid type, use the default message
			message = translate(this.locale, 'chart.unavailable');
		}

		this.clearPanels();
		this.setCursor(cursorTypes.DEFAULT);
		this.loadingStop(false); // Don't emit BOUNDARY_STATUS event
		this.showMessage(`<div class="modcharts-chartnotavailable">${message}</div>`);

		// Emit the event with the description if available
		this.eventEmitter.emit(eventTypes.CHART_NOT_AVAILABLE, {
			message,
			description
		});
	}

	/** Determines current visible data, and, if we're missing any historic data, retrieves it. */
	backfillData() {
		const domain = this.panels[0].xAxis.primaryScale.domain();
		const self = this;
		const domainLeft = Math.max(0, Math.floor(domain[0]));
		const domainRight = Math.min(
			this.exchangeDates
				? this.exchangeDates.length - 1
				: Math.floor(domain[1]),
			Math.floor(domain[1])
		);
		const leftIndex = this.params.returnPreviousPoint && this.dataPrimary.length > 1 ? 1 : 0;
		const dataLeft = this.closestExchangeIndex(this.dataPrimary[leftIndex].date);
		const isIntraday =
				typeof this.state.isIntraday === 'boolean'
					? this.state.isIntraday
					: /minute|hour/i.test(this.params.dataPeriod);
		const dayLimit = isIntraday ? this.limitIntradayCalendar : this.params.rulerInterdayStart;
		const dataNormalized = this.panels[0].isNormalized;
		const now = new Date();
		const dateLimit = new Date(now.setDate(now.getDate() - dayLimit));
		const dateDomainLeft =
				this.exchangeDates && this.exchangeDates[domainLeft]
					? this.getUTCDate(
						new Date(this.exchangeDates[domainLeft])
					)
					: null;

		const dateDataLeft = this.dataPrimary[leftIndex].date;
		// backfill only if domain's left is less than data's left AND if data's left is within the dateLimit time period (e.g. 35 years on interday).
		if (
			domainLeft < dataLeft &&
			dateDataLeft > dateLimit
		) {
			const dayDiff = (dateDataLeft - dateDomainLeft) / 1000 / 60 / 60 / 24;
			const offsetDays = Math.max(
				1,
				Math.ceil((new Date() - dateDataLeft) / 1000 / 60 / 60 / 24)
			);

			// invalidate if we haven't panned over enough based on a threshold of days (different for each period)
			let isValid = dayDiff >= this.backfillThreshold;

			// invalidate lengthy inter/intraday requests
			if (isIntraday && offsetDays > dayLimit) {
				isValid = false;
			}

			if (isValid) {

				// set (or reset) dateStart/Stop to the currently visible date range on the axis (this includes the empty gap about to be filled).  This preserves the start/stop dates if indicators are added or the chart is saved.
				this.params.dateStart = new Date(
					this.exchangeDates[domainLeft]
				);
				this.params.dateStop = new Date(
					this.exchangeDates[domainRight]
				);
				// limit offsetDays by the dayLimit
				this.params.endOffsetDays = Math.min(dayLimit, offsetDays);

				// if we're looking at more than one symbol, and we've panned over, define the normalizedate
				if (!this.params.normalizeDate && dataNormalized) {
					this.params.normalizeDate = this.dataPrimary[0].date;
				}

				this.loadData(() => {
					// reset endOffsetDays
					self.params.endOffsetDays = 0;
				}, { silent: false, prepend: true });
			}
		}
	}

	/**
	 * Determine when to trigger a backfill after panning.
	 * A backfill won't trigger if the panned date is less than this number of days from the first data date.
	 */
	get backfillThreshold() {
		switch (this.params.dataPeriod) {
			case 'Month': return 30;
			case 'Week': return 7;
			default: return 0;
		}
	}

	/**
	 * get nearest exchangeDates index by Date
	 * @method closestExchangeIndex
	 * @param {Date} jsDate
	 * @return {int}
	 */
	closestExchangeIndex(jsDate) {
		const eD = this.exchangeDates;
		const val = jsDate.getTime();
		let idx = 0;
		let mid = 0;
		let done = false;
		let min = 0;
		let max = eD.length - 1;

		while (!done) {
			if (max - min > 1) {
				mid = Math.floor((max + min) / 2);

				if (eD[mid] <= val) {
					min = mid;
				} else {
					max = mid;
				}
			} else {
				idx = eD[min] >= val ? min : max;
				done = true;
			}
		}

		return idx;
	}

	/**
	 * Search dataset for the closest round index to a given domain value
	 * For example, a mouse operation may result in a floating point domain value of 7341.3926090283085.
	 * The method will do a binary search for the nearest OHLC datapoint to that value, and return that datapoint's index.
	 * If missing, the method will assign the value of data[x].dateIndex as it performs the search.
	 * The method does double-duty (represented by the optional 'key' input and the data input passed)
	 * If key is 'dateIndex', the search will be done against the main OHLC dataset.  This is used for snapping a floating point x-coordinate to specific round index (crosshair and tool events use this)
	 * If key is omitted or 'index', the search will be done against the labels dataset.  This is used by the labeler to determine which subset of the larger labels array to process.
	 * @param {Number} val Domain value (ex: 7341.3926090283085)
	 * @param {Object[]} data Dataset to parse
	 * @param {String} key Search against either 'index' or 'dateIndex' property in the 'data' array
	 * @private
	*/
	_getIndexByDomain(val, data, key = 'index') {
		if (!data || !data.length) return 0;

		let idx = 0;
		let mid = 0;
		let midV = 0;
		let min = 0;
		let max = data.length - 1;
		let done = false;
		let distMax = 0;
		let distMin = 0;
		const isKeyDateIndex = key === 'dateIndex';

		while (!done) {
			if (max - min <= 1) {
				// calculate dateIndex here if not already present
				if (isKeyDateIndex && typeof data[min][key] !== 'number') {
					data[min][key] = this.closestExchangeIndex(data[min].date);
				}

				// calculate dateIndex here if not already present
				if (isKeyDateIndex && typeof data[max][key] !== 'number') {
					data[max][key] = this.closestExchangeIndex(data[max].date);
				}

				distMin = Math.abs(data[min][key] - val);
				distMax = Math.abs(data[max][key] - val);

				idx = distMin < distMax ? min : max;
				done = true;
			} else {
				// calculate dateIndex here if not already present
				if (isKeyDateIndex && typeof data[mid][key] !== 'number') {
					data[mid][key] = this.closestExchangeIndex(data[mid].date);
				}

				mid = Math.floor((max + min) / 2);
				midV = data[mid][key];

				if (midV <= val) {
					min = mid;
				} else {
					max = mid;
				}
			}
		}

		return idx;
	}

	/**
	 * Given a search date and a dataset that includes .date properties, find the nearest
	 * datapoint to the search date, and return the array index of that match.
	 * @param {Object[]} data - A data array of objects with a date property
	 * @param {Date} date - JS date
	 * @param {Boolean} checkExact - Whether or or not to make sure the date matches exactly.
	 * Defaults to false.
	 * @return {Number} - Returns the closest array index to the given date
	 */
	_getIndexByDate(data, date, checkExact = false) {
		if (!date || !data.length) return 0;
		let closestIndex;
		let min = 0;
		let max = data.length - 1;
		let done = false;
		while (!done) {
			if (max - min <= 1) {
				const distMax = date - data[max].date;
				const distMin = date - data[min].date;
				closestIndex = Math.abs(distMax) < Math.abs(distMin)
					? max
					: min;
				done = true;
			} else {
				const mid = Math.floor((max + min) / 2);
				if (data[mid].date <= date) {
					min = mid;
				} else {
					max = mid;
				}
			}
		}

		if (checkExact) {
			return data[closestIndex].date.getTime() === date.getTime()
				? closestIndex
				: -1;
		}
		return closestIndex;

	}

	/**
	 * Find earliest data index by date.  Similar to _getIndexByDate, but favors the left-hand match.
	 * Eg., it won't snap to the right-hand match even when that is closer to the search date.
	 * This is useful for stepped indicators that need to stay on the left-hand match for the entire
	 * duration of a given horizontal line segment.
	 * Note: if the search date is greater than the last date in the series, it will return the final
	 * series index.
	 * @method _getPrevIndexByDate
	 * @param {Array} data
	 * @param {Date} date
	 * @return {int}
	 */
	_getPrevIndexByDate(data, date) {
		let lowerIdx;
		let min = 0;
		let max = (data.length) ? data.length - 1 : 0;
		let mid = 0;
		let done = false;
		if (!date || !data.length) { return min; }
		if (data[max].date < date) { return max; }
		while (!done) {
			if (max - min <= 1) {
				lowerIdx = min;
				done = true;
			} else {
				mid = Math.floor((max + min) / 2);
				if (data[mid].date <= date) {
					min = mid;
				} else {
					max = mid;
				}
			}
		}
		return lowerIdx;
	}

	/** Get slice of datasets that match the current domain */
	_getFrameData(domain = this.panels[0].xAxis.primaryScale.domain()) {
		const data = {};
		const ruler = this.exchangeDates;
		const rulerLength = ruler.length;
		const dateMin = new Date(
			ruler[
				Math.min(
					rulerLength - 1,
					Math.max(0, Math.floor(domain[0]))
				)
			]
		);
		const dateMax = new Date(
			ruler[
				Math.min(
					rulerLength - 1,
					Math.max(0, Math.floor(domain[1]))
				)
			]
		);
		let dataSymbol;
		let filteredSymbol;
		let dataSymbolEl;
		let isPreviousClose;
		let isEvent;
		let t;
		let tLen;
		let el;
		let elId;
		let elUid;
		let filteredSymbolEl;
		let thisFiltered;
		let thisMinIdx;
		let thisMaxIdx;

		// loop over each symbol
		for (const symbol in this.data) {
			if (Object.prototype.hasOwnProperty.call(this.data, symbol)) {
				dataSymbol = this.data[symbol];

				if (!data[symbol]) data[symbol] = {};

				filteredSymbol = data[symbol];

				// loop over indicators for this symbol (bollinger, sma, etc)
				for (elId in dataSymbol) {
					if (Object.prototype.hasOwnProperty.call(dataSymbol, elId)) {
						dataSymbolEl = dataSymbol[elId];
						isPreviousClose = elId === 'previousclose';
						isEvent = /dividends|earnings|splits|custom/.test(elId);
						let maxOffset = 0;

						// CW-1348 special case - ichimoku clouds' maxIdx will need to be bumped to the right
						// to accomodate the chikou series being bumped to the left during rendering.
						if (elId === 'ichimokucloud') {
							maxOffset = Math.max.apply(
								null,
								this.getIndicatorsById('ichimokucloud').map(row => row.getInput('mediumPeriod')
								)
							);
						}

						// loop over series (open, high, low, close) for this indicator
						for (elUid in dataSymbolEl) {
							if (Object.prototype.hasOwnProperty.call(dataSymbolEl, elUid)) {
								el = dataSymbolEl[elUid];

								if (!el.length) continue;

								if (!filteredSymbol[elId]) filteredSymbol[elId] = {};

								filteredSymbolEl = filteredSymbol[elId];

								if (isPreviousClose) {
									filteredSymbol.previousclose[elUid] = el;
								} else if (isEvent) {
									// events

									filteredSymbolEl[elUid] =
										dataSymbolEl[elUid];
								} else {
									thisMinIdx = this._getIndexByDate(
										el,
										dateMin
									);
									thisMaxIdx = this._getIndexByDate(
										el,
										dateMax
									);

									if (maxOffset) {
										thisMaxIdx += maxOffset;
									}

									// slice at different indexes for each element
									// -1/+2 is to ensure the data is wider than viewable panel
									filteredSymbolEl[elUid] = el.slice(
										Math.max(thisMinIdx - 1, 0),
										Math.min(
											thisMaxIdx + 2,
											rulerLength - 1
										)
									);

									thisFiltered = filteredSymbolEl[elUid];

									for (
										tLen = thisFiltered.length, t = 0;
										t < tLen;
										t++
									) {
										if (thisFiltered[t]) {
											// precalculate the dateIndex for fast lookups later
											if (
												typeof thisFiltered[t]
													.dateIndex === 'undefined'
											) {
												thisFiltered[
													t
												].dateIndex = this.closestExchangeIndex(
													thisFiltered[t].date
												);
											}

											// special case: precalculate the chikou dateIndex if necessary (CW-2212)
											if (
												thisFiltered[t].dateChikou &&
												typeof thisFiltered[t]
													.dateIndexChikou ===
													'undefined'
											) {
												thisFiltered[
													t
												].dateIndexChikou = this.closestExchangeIndex(
													thisFiltered[t].dateChikou
												);
											}
										}
									}
								}
							}
						}
					}
				}
			}
		}

		return data;
	}

	/**
	 * similar to _getFrameData, this will return the slice of custom datasets that match the current domain.
	 */
	_getCustomFrameData(domain = this.panels[0].xAxis.primaryScale.domain()) {
		const data = {};
		const ruler = this.exchangeDates;
		const rulerLength = ruler.length;
		const dateMin = new Date(
			ruler[
				Math.min(
					rulerLength - 1,
					Math.max(0, Math.floor(domain[0]))
				)
			]
		);
		const dateMax = new Date(
			ruler[
				Math.min(
					rulerLength - 1,
					Math.max(0, Math.floor(domain[1]))
				)
			]
		);
		let t;
		let tLen;
		let el;
		let thisMinIdx;
		let thisMaxIdx;

		// loop over each custom dataset
		for (const dataset in this.data) {
			if (Object.prototype.hasOwnProperty.call(this.data, dataset)) {
				el = this.data[dataset];

				if (!el.length) continue;

				thisMinIdx = this._getIndexByDate(el, dateMin);
				thisMaxIdx = this._getIndexByDate(el, dateMax);

				el = el.slice(
					Math.max(thisMinIdx - 1, 0),
					Math.min(thisMaxIdx + 1, rulerLength)
				);

				for (tLen = el.length, t = 0; t < tLen; t++) {
					if (el[t]) {
						el[t].dateIndex = this.closestExchangeIndex(el[t].date);
					}
				}

				data[dataset] = el;
			}
		}

		return data;
	}

	/**
	 * convert params into an input object ready to be posted to ChartAPI
	 * @method _getChartAPIInputs
	 */
	_getChartAPIInputs(args = {}) {
		let dataPeriod = this.params.dataPeriod || 'Day';
		let days = args.days || this.params.days || 365;
		let daysInput;

		if (isNaN(Number(days))) {
			days = this.params.days = 365;
			dataPeriod = this.params.dataPeriod = 'Day';
		} else {
			// REMOVE THIS CONVERSION IN THE FUTURE ONCE PARAMS ARE CONVERTED TO NUMBERS
			days = Number(days);
		}

		// calculate the days between params.dateStart/dateStop
		if (this.params.dateStart && this.params.dateStop) {
			// CW-2432 The days input calculated here will normally reflect the number of days between dateStart and dateStop.  However, this value may not be large enough in some situations - specifically when older comparisons are present and the user has continued panning beyond the inception date of the younger primary symbol.  For context, the API request will ultimately use both the "days" and "endOffsetDays" inputs to determine the date range to return.  Since the endOffsetDays value doesn't change once the inception date of the primary symbol is reached, future pans will incorrectly request the same range from the API repeatedly.  To handle this, the dateStop input should use the first date of the primary symbol's dataset, rather than the date defined by the right-hand edge of the XAxis.
			let dateStop = this.params.dateStop;
			if (this.dataPrimary?.length) {
				dateStop = Math.max(dateStop, this.dataPrimary[0].date);
			}
			days =
				Math.ceil(
					(dateStop - this.params.dateStart) / 1000 / 60 / 60 / 24
				);
		}

		// earliest days value (via params.days or dateStart)
		const daysMin = Math.max(1, this.params.dateStart
			? Math.ceil((new Date() - this.params.dateStart) / 1000 / 60 / 60 / 24)
			: days
		);

		// if not a prepend request, we will request all days from dateStart until present
		// if a prepend request, just request the days that fall between dateStart/Stop, and include an additional endOffsetDays
		if (this.state.isIntraday) {
			const endOffset = this.params.rulerIntradayStart - (this.params.endOffsetDays || 0);
			const numWeekend = Math.floor(endOffset / 7);
			daysInput = Math.min(
				endOffset - (2 * numWeekend),
				args.prepend ? days : daysMin
			);
		} else {
			daysInput = Math.min(
				this.params.rulerInterdayStart - (this.params.endOffsetDays || 0),
				args.prepend ? days : daysMin
			);
		}

		// number of days to return
		daysInput = Math.max(1, daysInput);

		// initialize the chartapi input package
		const inputs = {
			days: daysInput,
			dataNormalized: this.panels[0].isNormalized,
			dataPeriod,
			dataInterval: Number(this.params.dataInterval || 1),
			realtime: this.params.realtime,
			yFormat: this.getChartAPIDataPrecision(),
			timeServiceFormat: 'JSON', //  requires MOD.Web.ChartAPI 0.1.75-beta
			rulerIntradayStart: this.params.rulerIntradayStart,
			rulerIntradayStop: this.params.rulerIntradayStop,
			rulerInterdayStart: this.params.rulerInterdayStart,
			rulerInterdayStop: this.params.rulerInterdayStop
		};

		// prepend request inputs
		if (args.prepend && this.params.endOffsetDays) {
			// endOffsetDays. (offset days from last trading day)
			inputs.endOffsetDays = this.params.endOffsetDays;
			if (this.state.isIntraday) {
				// exchangeOffset.  only needed on intraday. (CW-322)
				inputs.exchangeOffset = this.getExchangeOffsetInput();
			}
		}

		// returnDateType
		if (/chartapi\/series/i.test(this.params.apiSeries)) {
			inputs.returnDateType = 'ISO8601';
		}

		// isMax
		if (this.params.isMax) {
			inputs.isMax = true;
		}

		// set optional dataInterface
		if (this.params.dataInterface) {
			inputs.dataInterface = this.params.dataInterface;
		}

		// set optional feedSelection Group
		if (this.params.feedGroup) {
			inputs.feedGroup = this.params.feedGroup;
		}

		// optional usage reporting feature type
		if (this.params.usageReportingAppFeatureType) {
			inputs.usageReportingAppFeatureType = this.params.usageReportingAppFeatureType;
		}

		// major currency flag
		if (this.params.showMajorCurrency) {
			inputs.showMajorCurrency = true;
		}

		// poll date (optional).  the data api will limit its next response by this date or later.
		if (args.silent && this.dataPrimary.length) {
			const pollDate = new Date(
				this.dataPrimary[this.dataPrimary.length - 1].date
			);

			// account for client timezone offset
			pollDate.setMinutes(
				pollDate.getMinutes() - pollDate.getTimezoneOffset()
			);

			// pollDate input expects MSDate format
			inputs.pollDate = this.jsToMsDate(pollDate);
		}

		if (this.params.extendedHours) {
			inputs.tradeType = 'NormalAndExtendedHours';
		}

		// normalize date
		if (!inputs.dataNormalized) {
			this.params.normalizeDate = null;
			this.params.normalizeValue = null;
			this.params.normalizeValues = null;
		} else if (this.params.normalizeDate) {
			const normalizeDate = new Date(this.params.normalizeDate);

			// account for client timezone offset
			normalizeDate.setMinutes(
				normalizeDate.getMinutes() - normalizeDate.getTimezoneOffset()
			);

			inputs.normalizeDate = normalizeDate.toISOString();
		}

		// CH-1681 reduce API response size
		if (!this.state.isIntraday) {
			inputs.returnDateFormat = 'interday';
		}

		inputs.elements = this._getElementsInput();

		// CH-1799 customConfig
		inputs.customConfig = {
			Version: this.version,
			ReturnPreviousPoint: this.params.returnPreviousPoint
		};

		// reset params.isMax as this is only intended for a one-time use
		this.params.isMax = null;

		// special case: increase the interday XAxis if ichimoku is present (CH-1942)
		if (
			!this.state.isIntraday &&
			this.panels[0].indicators.filter(i => i.params.id === 'ichimokucloud').length > 0
		) {
			inputs.rulerInterdayStop = Math.max(365, inputs.rulerInterdayStop);
		}

		return inputs;
	}

	/**
	 * Get dynamic exchangeOffset value for correct intraday quote requests (CW-322)
	 * @method getExchangeOffsetInput
	 */
	getExchangeOffsetInput() {
		// use hardcoded number if passed as an input (this is for backward compatibility)
		if (this.params.exchangeOffset) {
			return this.params.exchangeOffset;
		}

		// or else calculate the dynamic exchangeOffset
		const utcOffset = this.xref[this.params.symbol]
			? this.xref[this.params.symbol].utcOffset || 0
			: 0;
		const earliestOpen = this.state.typicalSessions
			? this.state.typicalSessions.min || 0
			: 0;

		if (earliestOpen - utcOffset < 0) {
			return Math.abs(earliestOpen - utcOffset);
		}

		return 0;
	}

	/**
	 * convert dataPrecision into format string
	 * @method getChartAPIDataPrecision
	 */
	getChartAPIDataPrecision() {
		let format = '0.#';
		const xLen =
				typeof this.params.dataPrecision === 'number'
					? Math.max(0, this.params.dataPrecision - 1)
					: 2;

		for (let x = 0; x < xLen; x++) {
			format += '#';
		}

		return format;
	}

	/**
	 * Returns elements in MOD.Web.ChartAPI input format
	 * @return {Object[]} Array of elements in the proper format
	 */
	_getElementsInput() {
		let newElement;
		let symbol;
		const elements = [];

		this.panels.forEach(panel => {
			const dataNormalized = panel.isNormalized;

			// add indicator elements (sma, bollingers)
			for (const indicator of panel.indicators) {
				symbol = indicator.params.symbol || this.params.symbol;

				// overlay indicators will be added in getOverlayIndicators
				if (indicator.params.parentUid || indicator.params.parentUID) {
					continue;
				}

				newElement = {
					Label: indicator.params.uid,
					Type: indicator.params.datasetId || indicator.params.id,
					Symbol: symbol,
					OverlayIndicators: this.getOverlayIndicators(
						panel,
						indicator
					),
					Params: this.getIndicatorParamsInput(panel, indicator)
				};

				// NXC-735 specify alternative currency for returned prices
				if (
					this.params.currency &&
					newElement['Symbol'] === this.params.symbol
				) {
					newElement['DesiredCurrency'] = this.params.currency;
				}

				// normalized price data needs a multiplier
				if (dataNormalized) {
					newElement['ValueMultiplier'] =
						indicator.params.valueMultiplier || 100;
					newElement['ValueFormat'] = '{0:#,##0.######}'; // see NXC-609
				}

				// optional currency cross rate flag (NXC-734)
				if (/\w+\|\w+/.test(symbol.toString())) {
					newElement['IsCurrencyCrossRate'] = true;
				}

				elements.push(newElement);
			}

			// add event elements (splits, dividends, etc)
			for (const event of panel.events) {
				newElement = {};

				if (event.params.id === 'custom') continue;

				newElement.Label = event.params.uid;
				newElement.Type = event.params.id;
				newElement.Symbol = this.params.symbol;
				newElement.Params = this.getIndicatorParamsInput(panel, event);

				// AC-281 Add optional DesiredCurrency value
				if (this.params.currency) newElement['DesiredCurrency'] = this.params.currency;

				elements.push(newElement);
			}
		});

		return elements;
	}

	/**
	 * collect inputs specific to each chartapi element (could be none)
	 */
	getIndicatorParamsInput(panel, obj) {
		const params = {};
		const inputs = obj.params.inputs || [];
		inputs.forEach(row => {
			if (
				row.name === 'WSODIssue' &&
				row.value === 'auto'
			) {
				params[row.name] = panel.core.params.symbol;
			} else if (row.value !== 'ohlc') {
				params[row.name] = row.value;
			}
		});

		// Add cumulativeadjustment inputs when panning
		if (obj.cumulativeAdjustment && this.params.endOffsetDays > 0) {
			params['adjustmentValue'] = obj.cumulativeAdjustment.value;
			params['adjustmentDate'] = obj.cumulativeAdjustment.date;
		}
		return params;
	}

	/**
	 * get overlay param inputs in newer json input format
	 */
	getOverlayIndicators(panel, indicator) {
		const uid = indicator.params.uid;
		const overlays = [];
		let ind = {};
		let parentUid = '';

		for (ind of panel.indicators) {
			parentUid = ind.params.parentUid || ind.params.parentUID;
			if (parentUid && parentUid === uid) {
				overlays.push({
					Label: ind.params.uid,
					Type: ind.params.id,
					Symbol: ind.params.symbol || this.params.symbol,
					DataSeries: indicator.params.dataSeries || indicator.params.id,
					Params: this.getIndicatorParamsInput(panel, ind)
				});
			}
		}
		return overlays;
	}

	/** Get a unique key for this indicator
	 * TODO: Is this still used?  Might be a deprecated Modcharts V1 thing.
	 */
	getElementKey(indicator) {
		return [
			indicator.params.uid,
			indicator.params.symbol || this.params.symbol,
			indicator.params.id
		].join('-');
	}

	/**
	 * Updates the domain of the xAxis scales after the domain has been initialized or reset to [0, 0].
	 * @param {number} start - The new start bound of the domain
	 * @param {number} stop - The new stop bound of the domain
	 */
	_updateXAxisScaleDomains(start, stop) {
		const [domainStart, domainStop] = this.xAxis.primaryScale.domain();

		if (
			(domainStart === 0 && domainStop === 0) ||
			start !== domainStart ||
			stop !== domainStop // the domain may have been modified due to the inclusion of the ichimokucloud indicator
		) {
			this.panels.forEach((panel, i) => {
				panel.xAxis.setScaleDomain({ start,	stop });

				// Only update the scale once since all panels use the same scale
				if (i === 0) {
					if (this.state.updateXAxisPresentScaleDomain) {
						/**
						 * CH-1719
						 * Update the present scale domain given the new exchangeDates collection.
						 * If we don't do this, the domain (which represents the exchangeDates indices)
						 * will refer to the old exchangeDates collection indicies instead of the new ones.
						 */
						this.state.updateXAxisPresentScaleDomain = false;

						// Get the updated domain by calculating the present frame data
						let domain;
						let domainData;

						if (this.state.useDataPrimaryPresentScaleUpdate) {
							this.state.useDataPrimaryPresentScaleUpdate = false;
							domainData = this.state.presentDataPrimary;
						} else {
							/* This path requires calculating a new zoom transform for seamless use of pan/zoom */

							// Get current frame domain without adjustments, to be used in calculating proper timespan
							const [defaultStart, defaultStop] = this.getDefaultDomain(this.dataPrimary, [0, 0], false);

							// Use the number of days to calculate what the domain bounds should be
							const lastDate = new Date(this.dataPrimary[this.dataPrimary.length - 1].date);
							let firstDate = new Date(lastDate);

							// Use days if that's available, if not, get trading days between trading dates
							if (this.params.days) {
								firstDate.setDate(firstDate.getDate() - this.params.days);
							} else {
								const limit = calcTradingDaysBetweenTradingDates(
									new Date(this.exchangeDates[defaultStart]),
									new Date(this.exchangeDates[defaultStop]),
									this.timeService.tradingDays
								);

								let count = 0;
								const reverseDataPrimary = this.dataPrimary.slice().reverse();
								let checkDate = new Date(lastDate);

								for (const datum of reverseDataPrimary) {
									// If datum is from day before checkDate, increase count toward limit, update checkDate
									if (checkDate.getTime() - datum.date.getTime() >= (1000 * 60 * 60 * 24)) {
										firstDate = new Date(checkDate);
										// If we hit limit, don't continue
										if (++count >= limit) {
											break;
										}

										// If we haven't hit the limit, update the checkDate
										checkDate = new Date(datum.date);
									}
								}
							}

							if (this.state.isIntraday) {
								// snap to the beginning/end of typical session for each first/last day, e.g. 9:30am/4:00pm
								domain = [
									this._getIntradaySessionIndex(firstDate, this.state.typicalSessions.min),
									this._getIntradaySessionIndex(lastDate, this.state.typicalSessions.max)
								];
							} else {
								domain = [
									this.closestExchangeIndex(firstDate),
									this.closestExchangeIndex(lastDate)
								];
							}

							// Get price data for current frame
							const frameData = this.params.customData
								? this._getCustomFrameData(domain)
								: this._getFrameData(domain);
							domainData = this.getFirstDataset(frameData[this.params.symbol].price);

							// CH-1759: There's some funkiness with being off by one day in certain scenarios. We might need to force data
							// to exclude market close from previous day so that domain is correctly calculated in getDefaultDomain.
							// if (this.state.isIntraday) domainData.shift();

							// Update the present dataPrimary collection (this gets used when adding comparisons)
							this.state.presentDataPrimary = domainData;
						}

						const presentDomain = this.getDefaultDomain(domainData, null, true, true);
						this.state.xAxisPresentScale = this.xAxis.primaryScale.copy().domain(presentDomain);

						// Calculate new zoom transform
						const newTransform = this.zoomUtil.getTransformFromScales(
							this.state.xAxisPresentScale,
							this.xAxis.primaryScale
						);

						// Save transform to allow for resetting view
						if (!this.state.initialTransform) this.state.initialTransform = newTransform;

						// Update the zoom center to show how the saved chart is different from the present chart
						this.zoomUtil.updateZoomCenter({ chart: this, transform: newTransform });
					} else {
						// Save dataPrimary, present xAxis scale for calculating zoom transform updates
						this.state.xAxisPresentScale = this.xAxis.primaryScale.copy();
						this.state.presentDataPrimary = [...this.dataPrimary];

						// Save zoomIdentity to allow for resetting view
						this.state.initialTransform = zoomIdentity;
					}
				}
			});
		}
	}

	/**
	 * similar to _processChartApiData, this will convert all custom datasets into the native date format, also
	 * ensuring date inputs are parsed as Date objects
	 */
	_processCustomData(dataIn) {
		const data = {};
		let x;
		let d;
		let datapoint;
		Object.keys(dataIn).forEach(customId => {
			const customRows = dataIn[customId];
			data[customId] = [];

			for (x = 0; x < customRows.length; x++) {
				d = customRows[x];

				datapoint = {};
				Object.keys(d).forEach(key => {
					const value = d[key];
					if (key === 'date') {
						if (
							/^\d{5}$/.test(value.toString()) ||
							/\d{5}\.\d+/.test(value.toString())
						) {
							datapoint.date = this.msToJsDate(value);
						} else {
							datapoint.date = new Date(value);
							// adjust for local timezone (CH-1917)
							datapoint.date.setMinutes(
								datapoint.date.getMinutes() + datapoint.date.getTimezoneOffset()
							);
						}
					} else {
						datapoint[key] = value;
					}
				});

				// diff from previous neighbor (CW-1210)
				if (x > 0) {
					datapoint.diff = d.close - customRows[x - 1].close;
				} else {
					datapoint.diff = 0;
				}

				// hollow calc (CW-1210)
				if (typeof d.close === 'number' && typeof d.open === 'number') {
					datapoint.hollow = d.close > d.open ? 1 : 0;
				} else {
					datapoint.hollow = 0;
				}

				data[customId].push(datapoint);
			}
		});

		// Use the first non-empty custom dataset
		this.dataPrimary = this.getFirstCustomDataset(data);

		return data;
	}

	/**
	 * Convert dates into legacy structure
	 */
	convertSeriesDataDates(dates) {
		return dates;
	}

	/**
	 * Convert newer chartapi data format into legacy structure
	 */
	convertSeriesDataResponse(dataIn) {
		const components = dataIn.Elements;
		const elements = {};
		let label;

		if (dataIn.Dates) {
			dataIn.Dates = this.convertSeriesDataDates(dataIn.Dates);
		}

		for (const component of components) {
			label = (component.Label || component.Id).split('-')[0];
			elements[label] = this.convertSeriesComponent(component);
		}

		dataIn.Elements = elements;

		delete dataIn.ComponentSeries;

		return dataIn;
	}

	/**
	 * extract component data from API value.
	 */
	convertSeriesComponent(component) {
		const componentOut = {};
		const label = (component.Label || component.Id).split('-')[0];
		const series = {};
		component.ComponentSeries.forEach(seriesItem => {
			series[seriesItem.Type.toLowerCase()] = {
				min: seriesItem.MinValue,
				max: seriesItem.MaxValue,
				minDate: seriesItem.MinValueDate,
				maxDate: seriesItem.MaxValueDate,
				values: seriesItem.Values,
				dates: this.convertSeriesDataDates(seriesItem.Dates || [])
			};
		});

		componentOut[component.Type] = {
			OverlayIndicators: [],
			CompanyName: component.CompanyName,
			Currency: component.Currency,
			Dates: this.convertSeriesDataDates(component.Dates || []),
			DisplaySymbol: component.DisplaySymbol,
			ExchangeId: component.ExchangeId,
			Error: component.Error,
			Id: label,
			IssueType: component.IssueType,
			Positions: null,
			QuoteTimeLast: -1,
			Series: series,
			Status: component.Status,
			StatusString:
				component.Message?.length
					? component.Message
					: component.Status === 1
						? 'Success'
						: '',
			Symbol: component.Symbol,
			TimeZoneLabel: component.TimeZoneLabel,
			TimingData: component.TimingData,
			TimingRender: component.TimingRender,
			Type: component.Type,
			UtcOffsetMinutes: component.UtcOffsetMinutes,
			XSegments: null
		};

		// Price indicators will have a NormalizeValues property (CH-4821)
		if (component.NormalizeValues) {
			componentOut[component.Type].NormalizeValues = component.NormalizeValues;
		}

		// store metadata in separate collection
		if (component.Meta) {
			this.metaData[component.Label] = component.Meta;
		}

		// store timezone label
		if (
			component.Symbol === this.params.symbol &&
			component.TimeZoneLabel
		) {
			this.state.timezoneLabel = component.TimeZoneLabel;
		}

		if (component.OverlayIndicators) {
			for (const overlay of component.OverlayIndicators) {
				componentOut[component.Type].OverlayIndicators.push(
					this.convertSeriesComponent(overlay)
				);
			}
		}

		return componentOut;
	}

	/**
	 * The final stage of loading new data:
	 * process ChartAPI response
	 * convert response to new api format
	 * precalculate label datasets
	 * update parent class x-scales
	 * redraw
	 * @param {Object} dataIn
	 * @param {String} error
	 * @param {Function} callback
	 * @param {Object} args
	 * @return {Function}
	 */
	_processChartApiResponse(dataIn, error, callback, args = {}) {

		if (error) {
			return this._processChartApiNullResponse(
				dataIn,
				error,
				callback,
				args
			);
		}

		// CISC-1309 Update ruler thresholds if they differ from client side params.
		// This should be rare, but can occur if the ruler thresholds were updated on the server side, like when restricting
		// the size of the XAxis for certain user tiers.  The below will keep the client side params in sync.
		// So far only rulerInterdayStart is needed, but the rest could be added if necessary.
		if (dataIn?.MetaData?.RulerInterdayStart) {
			this.params.rulerInterdayStart = Number(dataIn.MetaData.RulerInterdayStart);
		}

		// convert from chartapi format
		if (dataIn?.Elements && typeof dataIn.Elements.length === 'number') {
			dataIn = this.convertSeriesDataResponse(dataIn);
		}

		// get result in modcharts format
		const result = this._processChartApiData(dataIn, args);

		if (
			this.timeService.status === 0 &&
				dataIn.TimeService.Status === 0 &&
				!args.silent
		) {
			// timeservice request was null
			return this._processChartApiNullResponse(
				dataIn,
				error,
				callback,
				args
			);
		} else if (
			!this.dataPrimary ||
				this.dataPrimary.length === 0 ||
				result === null
		) {
			// main dataset was null
			return this._processChartApiNullResponse(
				dataIn,
				error,
				callback,
				args
			);
		} else if (
			this.params.minMarketOpen &&
			this.params.days === 1 &&
			this.dataPrimary.length === 1 &&
			!args.silent
		) {
			// 1 day charts with only a single available datapoint should re-request with 2 days (CW-505)
			this.resetDomain();

			// update days param
			this.params.days = 2;

			return this.loadData(null, {
				days: 2
			});

		} else if (
			this.dataPrimary.length < 2 &&
				!Object.keys(this.xAxisDomainAdjustmentCategories).length &&
				!args.silent
		) {
			// line/mountain charts shouldn't attempt to draw until we have at least two datapoints
			return this._processChartApiNullResponse(
				dataIn,
				error,
				callback,
				args
			);
		}

		// create exchangeDates collection based on the params.days and/or first/last data dates
		if (!this.exchangeDates || !this.exchangeDates.length) {
			this.exchangeDates = this.panels[0].xAxis.getExchangeRuler();

			// create a full collection of fresh labels.
			this.panels[0].xAxis.labeler._createLabels();
		}

		// valid data result.  either prepend, append, or replace data
		if (args.prepend) {
			// prepend to beginning (backfill)
			this.data = this.prependChartAPIResponse(result, this.data);
		} else if (args.silent) {
			// append to end (polling)
			this.data = this.appendChartAPIResponse(result, this.data);
		} else {
			// replace everything (all new data)
			this.data = result;
		}

		// calculate the diff/hollow properties here so they don't have to be recalculated during renders
		this.data = this.getCandleData(this.data);

		// clean up any indicators associated with invalid/failed symbols
		this.data = this.processInvalidSymbols(this.data);

		// get an appropriate xAxis domain for this data
		const domain = this.getDefaultDomain();

		// Update each xAxis domain
		this._updateXAxisScaleDomains(domain[0], domain[1]);

		// resize / set panel dimensions
		if (!args.silent) this.resize();

		const status = (error || result === null) && !args.prepend ? 0 : 1;

		args = { status, ...args };

		// optionally set up a once() listener for the next render event
		this.handlePanelYAxisAutoWidth();

		// render
		this.postProcessRender(args);

		this.panels.forEach(panel => {
			try {
				panel.updateLegend();
			} catch (e) {
				console.error('Error updating legend for panel', panel, e);
			}
		});

		this.loadingStop();

		this.setZoomEnabled(this.params.zoomEnabled);

		// increment load count
		this.state.dataLoadCount += 1;

		// reset 401 counter
		this.state.invalidTokenCount = 0;

		// inform whomever that data request has completed, and include the final point data
		if (this.dataPrimary.length && this.dataPrimary[this.dataPrimary.length - 1].date) {
			// exclude some variables from the regular crosshair data
			// eslint-disable-next-line no-unused-vars
			const { activePanel, coords, panels, ...rest } = buildCrosshairData(
				this.panels[0],
				this.dataPrimary[this.dataPrimary.length - 1].date
			);
			args.lastDataPoint = { ...rest };
		}

		this.eventEmitter.emit(eventTypes.DATA_LOAD_COMPLETE, args);

		// data density estimation
		this.eventEmitter.emit(eventTypes.DATA_DENSITY, this.dataDensity);

		if (callback) return callback(this);
	}

	// If using auto YAxis width, then - a single time - do a second render to pick up flag widths.
	// I couldn't see a better way to do this, since there's a chicken-and-egg situation where the
	// flags need to render first before we can measure their width, and then update the panel
	// padding on the second render pass.  This will add ~16ms to the first load time.
	handlePanelYAxisAutoWidth() {
		if (
			this.params.panelYAxisAutoWidth &&
			!this.eventEmitter.listeners('FLAG_RENDER_LASTCLOSE').length // if custom flags are used, don't try to auto-size since we don't know the custom flag width
		) {
			// note the once(), not on()
			this.eventEmitter.once(eventTypes.RENDER, () => {
				this.resize();
				this.render();
			});
		}
	}

	/** Render the chart after processing data */
	postProcessRender(args) {
		const animateType = this.params.animation;
		const loadCount = this.state.dataLoadCount;

		// render chart (normal or animated)
		if (animateType && !args.silent && !args.prepend) {
			if (animateType === 'first' && loadCount === 0) {
				// first time animation
				this.animate();
			} else if (animateType === 'staged' && loadCount === 0) {
				// stage the animation but don't fully render
				this.render({ clipWidth: 0 });
			} else if (animateType === 'all') {
				// animate new data
				this.animate();
			} else {
				// default render
				this.render();
			}
		} else {
			// default render
			this.renderQueue();
		}
	}

	/**
	 * handle several types of data failures
	 */
	_processChartApiNullResponse(dataIn, error, callback, args) {
		this.loadingStop(false); // Don't emit BOUNDARY_STATUS event

		// track the count of consecutive 401s
		if (error && error.status === 401) {
			this.state.invalidTokenCount++;
		}

		const statusReturn = {
			status: 0,
			statusMsg: '',
			invalidTokenCount: this.state.invalidTokenCount
		};

		if (args.prepend) {
			statusReturn.statusMsg = 'Prepend error - result was null';
			this.eventEmitter.emit(eventTypes.DATA_PREPEND_ERROR, {
				status: statusReturn,
				error
			});
		} else if (args.silent) {
			statusReturn.statusMsg = `Poll error ${new Date()}`;
			this.eventEmitter.emit(eventTypes.DATA_POLL_ERROR, {
				status: statusReturn,
				error
			});
		} else {
			this.status = 0;
			this.clearPanels();
			this.data = null;
			this.showMessageChartNotAvailable({
				description: 'Invalid chart data response',
				error
			});

			statusReturn.statusMsg = `Invalid chart data : ${JSON.stringify(error || {})}`;
			this.eventEmitter.emit(eventTypes.DATA_LOAD_ERROR, {
				status: statusReturn,
				error
			});
		}

		// clear ongoing XHR reference
		if (this.xhrCancelSource) {
			this.xhrCancelSource.cancel();
		}
		this.dataXHR = null;

		// re-enable zoom
		this.setZoomEnabled(this.params.zoomEnabled);

		// inform whomever that data request has completed
		this.eventEmitter.emit(eventTypes.DATA_LOAD_COMPLETE, args);

		this.warn({
			status: statusReturn,
			error
		});

		if (callback) return callback(statusReturn.status);

		return statusReturn;
	}

	// invalid indicators
	// if a symbol was invalid, remove any indicators assigned to it, and remove from symbolCompare collection
	processInvalidSymbols(data) {
		const panel = this.panels[0];

		if (this.params.symbolCompare.length) { // backward compatibility (CH-1504)
			for (let i = this.params.symbolCompare.length - 1; i >= 0; i--) {
				const symbolCompare = this.params.symbolCompare[i];
				if (!data[symbolCompare]) {
					this.params.symbolCompare.splice(i, 1);
					for (let x = panel.indicators.length - 1; x >= 0; x--) {
						if (panel.indicators[x].params.symbol === symbolCompare) {
							panel.removeIndicator(panel.indicators[x]);
						}
					}
				}
			}
		} else {
			for (let i = this.comparisons.length - 1; i >= 0; i--) {
				if (!data[this.comparisons[i]]) {
					for (let x = panel.indicators.length - 1; x >= 0; x--) {
						if (panel.indicators[x].params.symbol === this.comparisons[i]) {
							panel.removeIndicator(panel.indicators[x]);
						}
					}
				}
			}
		}

		return data;
	}

	// add result to beginning of current dataset
	prependChartAPIResponse(result, data) {
		Object.keys(data).forEach(symbol => {
			const resultSymbolData = result[symbol];
			if (resultSymbolData) {
				Object.entries(resultSymbolData).forEach(([row, dataPoint]) => {
					// row === "price"
					Object.entries(dataPoint).forEach(([uid, series]) => {
						// data.GE.price.<uid>
						if (series && series.length) {
							for (let x = series.length - 1; x >= 0; x--) {
								const newDate = series[x].date;
								const dataSet = data[symbol] && data[symbol][row] && data[symbol][row][uid];
								// only splice in the new datapoint if it's earlier than the first date
								if (dataSet) {
									if (!dataSet.length) {
										dataSet.push(series[x]);
									} else if (
										newDate < dataSet[0].date
									) {
										dataSet.splice(
											0,
											0,
											series[x]
										);
									}
								}
							}
						}
					});
				});
			}
		});

		return data;
	}

	// add result to end of current dataset
	appendChartAPIResponse(result, data) {
		let x = 0;
		let currData = [];

		Object.keys(data).forEach(symbol => {
			if (result[symbol]) {
				Object.keys(result[symbol]).forEach(row => {
					// row === "price"
					const dataPoint = result[symbol][row];
					Object.keys(dataPoint).forEach(uid => {
						const series = dataPoint[uid];
						// this === this.data.GE.price.<uid>

						currData = data[symbol][row][uid];

						for (x = 0; x < series.length; x++) {
							if (!currData.length) {
								// append the new datapoint if it's new
								currData.push(series[x]);
							} else if (
								series[x].date -
									currData[currData.length - 1].date ===
								0
							) {
								// replace the last datapoint if it's == last existing date
								currData[currData.length - 1] = series[x];
							} else if (
								series[x].date >
								currData[currData.length - 1].date
							) {
								// append the new datapoint if it's > last existing date
								currData.push(series[x]);
							}
						}
					});
				});
			}
		});

		return data;
	}

	/**
	 * similar to _processChartApiResponse, this is the final stage of loading a chart with custom data.
	 * errors are handled, "this.data" is populated (via _processCustomData), panel domains reset,
	 * exchangeDates collection is created, label collections created, loading graphic removed and final callbacks called.
	 */
	_processCustomResponse(dataIn, error, callback, args = {}) {
		let isValid = true;

		if (error) {
			this.warn(`XHR error ${error.status || '-1'}: ${error.statusText || 'Unknown status'}`);
			this.warn(
				error.responseText ? error.responseText : 'Unknown error'
			);
			isValid = false;
		} else {
			this.clearMessage();

			// ensure customData contains at least one valid dataset
			const validationResult = this._validateCustomData(dataIn);

			if (!validationResult.isValid) {
				// console.error('Validation errors:', validationResult.errors);

				if (Object.keys(validationResult.validDatasets).length === 0) {
					// If there are no valid custom datasets, treat it as a null response
					return this._processChartApiNullResponse(dataIn, error, callback, args);
				}
			}

			// valid data result.  replace this.data
			this.data = this._processCustomData(dataIn, args);

			// create exchangeDates collection
			if (!this.exchangeDates || !this.exchangeDates.length) {
				this.exchangeDates = this.panels[0].xAxis.getCustomRuler(this.getFirstCustomDataset(this.data));

				// create a full collection of fresh labels
				this.panels[0].xAxis.labeler._createLabels();
			}

			const domain = this.getDefaultDomain();

			// Update each xAxis domain
			this._updateXAxisScaleDomains(domain[0], domain[1]);

		}

		// resize / set panel dimensions
		this.resize();

		const status = error && !args.prepend ? 0 : 1;

		args = { ...{ status }, ...args };

		this.status = status;

		// don't attempt to render invalid datasets
		if (isValid) this.postProcessRender(args);

		this.panels.forEach(panel => panel.updateLegend());

		this.loadingStop(isValid); // Check to see if BOUNDARY_STATUS event should be emitted

		this.setZoomEnabled(this.params.zoomEnabled);

		this.eventEmitter.emit(eventTypes.DATA_LOAD_COMPLETE, args);

		if (callback) return callback(this);
	}

	/**
	 * Validate the custom dataset
	 * @param {object} data
	 * @returns {Object} An object containing valid datasets and a list of errors
	 */
	_validateCustomData(data) {
		const result = {
			isValid: true,
			validDatasets: {},
			errors: []
		};

		if (!data || typeof data !== 'object' || !Object.keys(data).length) {
			const errorMsg = 'No custom data found or data is not an object';
			this.warn(errorMsg);
			result.isValid = false;
			result.errors.push(errorMsg);
			return result;
		}

		const domainAdj = Object.keys(this.xAxisDomainAdjustmentCategories || {}).length;

		for (const [key, value] of Object.entries(data)) {
			let datasetIsValid = true;

			// Validate key
			if (typeof key !== 'string' || key.trim().length === 0) {
				const errorMsg = `Invalid custom data key: '${key}'`;
				this.warn(errorMsg);
				result.errors.push(errorMsg);
				datasetIsValid = false;
			}

			// Validate value
			if (!Array.isArray(value)) {
				const errorMsg = `Custom dataset '${key}' is not an array`;
				this.warn(errorMsg);
				result.errors.push(errorMsg);
				datasetIsValid = false;
			} else {
				if (value.length === 0) {
					const errorMsg = `Custom dataset '${key}' has no datapoints`;
					this.warn(errorMsg);
					result.errors.push(errorMsg);
					datasetIsValid = false;
				} else if (!domainAdj && value.length < 2) {
					const errorMsg = `Custom dataset '${key}' must contain at least two datapoints when using line or fill markerTypes`;
					this.warn(errorMsg);
					result.errors.push(errorMsg);
					datasetIsValid = false;
				}
			}

			// If dataset is valid, add it to the validDatasets object
			if (datasetIsValid) {
				result.validDatasets[key] = value;
			} else {
				// Mark overall result as invalid if any dataset fails
				result.isValid = false;
			}
		}

		// Check if there are any valid datasets
		if (Object.keys(result.validDatasets).length === 0) {
			const errorMsg = 'All datasets are invalid. No valid custom data available.';
			this.warn(errorMsg);
			result.errors.push(errorMsg);
			result.isValid = false;
		}

		return result;
	}

	/**
	 * convert local date to UTC
	 * @method getUTCDate
	 * @param {Date} date
	 * @return {Date}
	 */
	getUTCDate(date) {
		date = new Date(date);

		return new Date(
			date.getUTCFullYear(),
			date.getUTCMonth(),
			date.getUTCDate(),
			date.getUTCHours(),
			date.getUTCMinutes(),
			date.getUTCSeconds()
		);
	}

	/**
	 * Convert arrays of numbers from raw strings or OA Dates
	 * @param {Object[]} data
	 * @param {Number} utcOffset The UTC offset in minutes (should be sourced from chartapi request)
	 * Note: if a 24-hour axis is needed, the utcoffset should be the same for all symbols.
	 * If symbols should all be plotted in exchange local time, pass individual respective offsets.
	 * The UTC offset is not applied to interday charts.
	 * @return {Object[]}
	 * @private
	 */
	convertChartApiDates(data, utcOffset) {
		const dataOut = [];
		let date;
		let hours;
		let minutes;
		let seconds;
		let pct;
		let i;
		const iLen = data.length;
		const intradayISO = /^(\d{4})-(\d\d)-(\d\d)T(\d\d):(\d\d):(\d\d)(\.*\d*)/;
		const interdayISO = /^(\d{4})-(\d\d)-(\d\d)$/;
		for (i = 0; i < iLen; i++) {

			if (interdayISO.test(data[i])) {

				date = new Date(
					Number(RegExp.$1),
					Number(RegExp.$2) - 1,
					Number(RegExp.$3)
				);

				if (this.state.isIntraday) {
					// utc offset to get dates back to local exchange time
					date.setUTCMinutes(date.getUTCMinutes() + utcOffset);
				}

				date.setTime(Math.round(date.getTime() / 60000) * 60000);
				dataOut.push(date);

			} else if (intradayISO.test(data[i])) {

				hours = Number(RegExp.$4);
				minutes = Number(RegExp.$5);
				seconds = Number(RegExp.$6);
				pct = Number(RegExp.$7);

				date = new Date(
					Number(RegExp.$1),
					Number(RegExp.$2) - 1,
					Number(RegExp.$3),
					hours,
					minutes,
					seconds
				);

				// if percent of second is > 0.5, round up to next second
				if (pct >= 0.5) {
					date.setUTCSeconds(date.getUTCSeconds() + 1);
				}

				if (this.state.isIntraday) {
					// utc offset to get dates back to local exchange time
					date.setUTCMinutes(date.getUTCMinutes() + utcOffset);
				}

				date.setTime(Math.round(date.getTime() / 60000) * 60000);

				dataOut.push(date);
			} else if (/^\d{5}$/.test(data[i]) || /\d{5}\.\d+/.test(data[i])) {
				date = this.msToJsDate(data[i]);

				if (this.state.isIntraday) {
					// utc offset to get dates back to local exchange time
					date.setUTCMinutes(date.getUTCMinutes() + utcOffset);
				}

				dataOut.push(date);
			}
		}

		return dataOut;
	}

	/**
	 * Convert OA Dates to JS
	 * @method msToJsDate
	 * @param {int} msDate
	 * @return {Date}
	 */
	msToJsDate(msDate) {
		const jO = new Date((msDate - 25569) * 86400000);
		const tz = jO.getTimezoneOffset();
		const ms = (msDate - 25569 + tz / (60 * 24)) * 86400000;

		// round to nearest second
		return new Date(Math.round(ms / 1000) * 1000);
	}

	/**
	 * Convert JS Date to OA Date
	 * @method jsToMsDate
	 * @param {Date} jsDate
	 * @return {int}
	 */
	jsToMsDate(jsDate) {
		return (
			(jsDate - new Date(Date.UTC(1899, 11, 30))) / (24 * 60 * 60 * 1000)
		);
	}

	/**
	 * convert dynamic ChartAPI json into modcharts standardized data structure.
	 * @method _processChartApiData
	 * @param {object} dataIn
	 * @param {object} [args]
	 * @return {object}
	 */
	_processChartApiData(dataIn, args = {}) {
		// check for any hard failures
		if (
			!dataIn ||
			!dataIn.Dates ||
			!dataIn.Elements ||
			(!args.prepend && !args.silent && !dataIn.TimeService)
		) {
			return null;
		}

		this._processChartApiElementsFailure(dataIn);

		// chartapi will return the normalizedate if used either implicitly in the service logic or explicitly as an input
		if (dataIn && dataIn.NormalizeDate) {
			if (/^\d{5}$/.test(dataIn.NormalizeDate.toString())) {
				this.params.normalizeDate = this.msToJsDate(
					dataIn.NormalizeDate
				);
			} else {
				this.params.normalizeDate = new Date(dataIn.NormalizeDate);
			}

			// deprecated, use dataIn.NormalizeValues instead
			if (dataIn.NormalizeCloseValue) {
				this.params.normalizeValue = Number(dataIn.NormalizeCloseValue);
			}

			// store API NormalizeValues - as of KB-739 this should always be returned.
			if (dataIn.NormalizeValues) {
				this.params.normalizeValues = dataIn.NormalizeValues;
			}
		}

		this.clearMessage();

		const elementsIn = this.getChartAPIElements(dataIn);
		const dataOut = this.createDataSeries(elementsIn, args);

		// parse timeservice data
		this.processTimeServiceData(dataIn.TimeService, args);

		// set primary data reference and overall status
		if (!args.prepend && !args.silent) {
			this.dataPrimary = this._getPrimaryDataReference(
				elementsIn,
				dataOut
			);
			this.status = this.dataPrimary.length ? 1 : 0;
		}

		return dataOut;
	}

	/**
	 * Process element-level failures
	 * @param {Object} data - Object with response information from chart data request
	 */
	_processChartApiElementsFailure(data) {
		if (
			data.Elements &&
			(typeof this.params.endOffsetDays === 'undefined' ||
				this.params.endOffsetDays === 0)
		) {
			Object.keys(data.Elements).forEach(key => {
				// Each item in Elements is a KVP with the indicator type as the key, and the element info
				// as the value. We need to look at the element info, so dig down one more level.
				const elementContainer = data.Elements[key];

				Object.keys(elementContainer).forEach(elementName => {
					const element = elementContainer[elementName];
					const indicator = this.getIndicatorByUid(element.Id);
					const panel = indicator && indicator.panel;

					// Don't warn on error status code 130 (Invalid indicator was requested). See CH-298, CH-349 for more info.
					if (element.Error && element.Error.Code !== '130') {
						this.warn(`Element error: ${element.Symbol || ''} : ${element.Error.Code} : ${element.Error.Message} : Element Id: '${elementName}'`);
						if (this.eventEmitter.listeners(eventTypes.DATA_ELEMENT_ERROR).length) {
							// Custom error handling
							this.eventEmitter.emit(eventTypes.DATA_ELEMENT_ERROR, {
								element,
								indicator,
								panel
							});
						} else if (panel && !panel.isUpper) {
							// Default error handling
							panel.renderErrorMessage(
								translate(
									this.locale,
									'chart.unavailableIndicator',
									[indicator ? indicator.params.name : 'Indicator']
								)
							);
						}
					} else if (panel) {
						// The indicator is working again (can happen if the symbol changed)
						panel.clearErrorMessage();
					}
				});
			});
		}
	}

	/**
	 * Flatten uid/elementId together for easier parsing and attach dates (this is step 1).
	 * Dates can live in three places: global, element-level, or series-level.
	 * The majority of indicators with series-level dates share the same dates across all series.
	 * @param {Object} data - Object with response information from chart data request
	 */
	getChartAPIElements(data) {
		const elements = [];
		let globalDates = [];
		let globalUTCOffset;

		// find global utc offset and generate globalDates
		Object.keys(data.Elements).forEach(symbol => {
			const dataSet = data.Elements[symbol];
			Object.keys(dataSet).forEach(field => {
				const value = dataSet[field];
				if (
					typeof globalUTCOffset !== 'number' &&
					value.Symbol === this.params.symbol &&
					value.Type === 'price'
				) {
					globalUTCOffset = value.UtcOffsetMinutes;
				}

				if (
					typeof globalUTCOffset !== 'number' &&
					this.xref[this.params.symbol] &&
					typeof this.xref[this.params.symbol].utcOffset === 'number'
				) {
					globalUTCOffset = this.xref[this.params.symbol].utcOffset;
				}

				// generate the globalDates collection once.
				if (
					!globalDates.length &&
					typeof globalUTCOffset === 'number'
				) {
					globalDates = this.convertChartApiDates(
						data.Dates,
						globalUTCOffset
					);
				}
			});
		});

		// extract elements from the returned data
		Object.keys(data.Elements).forEach(symbol => {
			const dataSet = data.Elements[symbol];
			Object.keys(dataSet).forEach(field => {
				const value = dataSet[field];
				// regular element
				elements.push(
					this.getChartAPIElement(
						value,
						data,
						globalUTCOffset,
						globalDates
					)
				);

				// child elements
				const overlayIndicators = value.OverlayIndicators || [];
				overlayIndicators.forEach(indicator => {
					elements.push(
						this.getChartAPIElement(
							this.getFirstDataset(indicator),
							data,
							globalUTCOffset,
							globalDates
						)
					);
				});
			});
		});

		return elements;
	}

	/**
	 * @method getChartAPIElement
	 * extract a single element from returned API data, and populate seriesDates for any collections that use
	 * element-level or series-level dates instead of global dates.
	 */
	getChartAPIElement(element, data, globalUTCOffset, globalDates) {
		let isValidElement = false;
		const newElement = {
			currency: element.Currency,
			uid: element.Id,
			series: {},
			seriesDates: {},
			issueType: element.IssueType,
			companyName: element.CompanyName,
			exchangeId: element.ExchangeId,
			status: element.Status,
			symbol: element.Symbol,
			displaySymbol: element.DisplaySymbol,
			type: element.Type,
			utcOffset: element.UtcOffsetMinutes,
			meta: element.Meta
		};

		// Price elements will return a NormalizeValues collection (CH-4821)
		// Store off element-level NormalizeValues into state for use in crosshair and export data later.  (we don't need to add it to the dataset series.)
		if (element.NormalizeValues) {
			if (!this.state.normalizeValues) {
				this.state.normalizeValues = {
					indicators: {}
				};
			}
			this.state.normalizeValues.indicators[element.Id] = element.NormalizeValues;
		}

		// Store company information in the xref collection for future use
		if (
			typeof newElement.utcOffset === 'number' &&
			newElement.exchangeId &&
			newElement.issueType &&
			newElement.symbol &&
			!this.xref[newElement.symbol]
		) {
			this.xref[newElement.symbol] = {
				companyName: newElement.companyName,
				currency: newElement.currency,
				exchangeId: newElement.exchangeId,
				issueType: newElement.issueType,
				meta: newElement.meta,
				displaySymbol: newElement.displaySymbol,
				utcOffset: newElement.utcOffset
			};
		}

		// get the union of all element dates and series dates
		const elDates = element.Dates && element.Dates.length;
		let datesCombined = elDates ? element.Dates : data.Dates;
		const elementDates = elDates
			? this.convertChartApiDates(element.Dates, globalUTCOffset)
			: null;

		// splits are the only events with series-level dates (AC-101)
		if (newElement.type === 'splits') {
			datesCombined = [];
		}

		// extract series data (eg. if the element.Type is 'price', it will have four 'Series': open, high, low, close)
		Object.keys(element.Series).forEach(seriesId => {
			const series = element.Series[seriesId];

			if (series.values && series.values.length) {
				const newSeriesDates = {};
				const newElementDates = {};
				let seriesDates = [];

				if (/psarbuy|psarsell/.test(seriesId)) {

					// special case: ignore psarbuy/psarsell series
					return true;

				} else if (seriesId === 'chikouspan') {

					// special case: chikouspan needs to retain its own dates (CW-3069)
					// see _getDataMap in the IchimokuCloudIndicator class for more info
					newElement.datesChikou = this.convertChartApiDates(
						series.dates,
						newElement.utcOffset
					);
					newElement.series[seriesId] = series.values;

				} else if (series.dates && series.dates.length) {

					// use series-level dates
					seriesDates = this.convertChartApiDates(
						series.dates,
						newElement.utcOffset
					);
					datesCombined = datesCombined.concat(series.dates);

					seriesDates.forEach((date, idx) => {
						newSeriesDates[date] = series.values[idx];
					});

					newElement.seriesDates[seriesId] = newSeriesDates;
					newElement.series[seriesId] = [];

				} else if (element.Dates && element.Dates.length) {

					datesCombined = datesCombined.concat(element.Dates);

					elementDates.forEach((date, idx) => {

						if (newElementDates[date]) { // duplicate element-level date. should only occur for events (CH-608)

							if (!Array.isArray(newElementDates[date])) {
								// convert to array
								newElementDates[date] = [newElementDates[date]];
							}

							newElementDates[date].push(series.values[idx]);

						} else { // normal element-level date

							newElementDates[date] = series.values[idx];
						}
					});

					newElement.seriesDates[seriesId] = newElementDates;
					newElement.series[seriesId] = [];

				} else {
					// regular element with no series/element dates
					newElement.series[seriesId] = series.values;
				}

				isValidElement = true;
			}
		});

		// sort and remove duplicates
		datesCombined = datesCombined
			.sort()
			.filter((item, pos, ary) => !pos || item !== ary[pos - 1]);

		// set element dates
		if (isValidElement) {
			if (!datesCombined.length) {
				// use global dates if there were no dates at the element or series level

				newElement.dates = globalDates;
			} else if (datesCombined.length) {
				// use custom dates. these will be either element-level dates, or a combination of series-level dates.

				newElement.dates = this.convertChartApiDates(
					datesCombined,
					globalUTCOffset
				);
			}
		}

		// store off currency
		this.state.currency = newElement.currency;

		return newElement;
	}

	/**
	 * @method createDataSeries
	 * step 2: now pivot elements into individual datapoints that contain a date and one value per series (eg. open, high, low, close)
	 * Note: use dates from the seriesDates collection where applicable.  These are series that have different dates than GlobalDates, either at series or element level.

			Before conversion:
		[
			{
				type: "price",
				symbol: <symbol>,
				dates: [<date>,<date>,...],
				series: {
					open: [1,2,3,4,...],
					high: [1,2,3,4,...],
					low: [1,2,3,4,...],
					close: [1,2,3,4,...]
				}
			},
			{
				type: "ichimokucloud",
				symbol: <symbol>,
				dates: [<date>,<date>,...],
				seriesDates: {
					"senkouspana": [{<date>: 13687.725},{<date>: 13687.725},{<date>: 13687.725},...],
					"senkouspanb: [{<date>: 13687.725},{<date>: 13687.725},{<date>: 13687.725},...]
				},
				series: {
					kijunsen: [1,2,3,4,...],
					tenkansen: [1,2,3,4,...],
					chikouspan: [1,2,3,4,...],
					senkouspana: [],
					senkouspanb: []
				}
			}
		]

			After conversion:
			{
			<symbol>: {
				"price":
					<uid>:
						[
							{
								date: <date>,
								dateIndex: <dateIndex>,
								open: 12.14,
								high: 22.31,
								low: 11.41,
								close: 22.30
							},
							{
								date: <date>,
								dateIndex: <dateIndex>,
								open: 13.14,
								high: 13.19,
								low: 12.98,
								close: 13.15
							}
						]
				},
				"ichimokucloud":
					<uid>:
						[
							{
								date: <date>,
								dateIndex: <dateIndex>,
								dateChikou: <date>,
								dateIndexChikou: <dateIndexChikou>,
								chikouspan: 41.31,
								kijunsen: 41.30,
								senkouspana: 29.41,
								senkouspanb: 20.11,
								tenkansen: 39.99
							},
							{
								date: <date>,
								dateIndex: <dateIndex>,
								dateChikou: <date>,
								dateIndexChikou: <dateIndexChikou>,
								chikouspan: 41.32,
								kijunsen: 41.29,
								senkouspana: 49.50,
								senkouspanb: 47.23,
								tenkansen: 40.21
							}
						]
				}
			}
			}

	*/
	createDataSeries(elements, args) {
		const dataOut = {};
		let newDatapoint = {};
		let isValid = false;
		let seriesValue = 0.0;

		// price, sma, volume etc.
		elements.forEach(element => {
			const symbol = element.symbol;
			const currency = element.currency;
			const uid = element.uid;

			if (!dataOut[symbol]) {
				dataOut[symbol] = {};
			}

			if (!dataOut[symbol][element.type]) {
				dataOut[symbol][element.type] = {};
			}

			// grouping by uid isn't really necessary and should be removed one day
			dataOut[symbol][element.type][uid] = [];

			// loop over each element's dates collection and collect into individual datapoints
			for (let i = 0; i < (element.dates || []).length; i++) {
				// only add new datapoints that contain date and value(s)
				isValid = false;

				// stub out a new datapoint with this date
				newDatapoint = {
					date: element.dates[i]
				};

				// add all series (open, high, low close) to this new datapoint
				Object.keys(element.series).forEach(seriesId => {
					const seriesItem = element.series[seriesId];
					// special case: ignore new previousclose values on prepends
					if (
						seriesId === 'previousclose' &&
						args.prepend &&
						!this.state.isIntraday
					) {
						return true;
					}

					if (element.seriesDates[seriesId]) {
						// special case: pull value from the seriesDates collection (eg. senkouspan, advancedecline, percentb, etc)
						seriesValue = element.seriesDates[seriesId][element.dates[i]];
						newDatapoint[seriesId] = typeof seriesValue !== 'undefined' ? seriesValue : NaN;
					} else {
						// default
						newDatapoint[seriesId] = seriesItem[i];
					}

					// special case: chikouspan needs to retain its own dates (CW-3069)
					// see _getDataMap in the IchimokuCloudIndicator class for more info
					if (
						seriesId === 'chikouspan' &&
						typeof element.datesChikou[i] !== 'undefined'
					) {
						newDatapoint.dateChikou = element.datesChikou[i];
					}

					isValid = true;
				});

				// add currency to dividend datapoints
				if (
					element.type === 'dividends' ||
					element.type === 'dividendscomplete'
				) {
					newDatapoint.currency = currency;
				}

				// if valid, append to main dataset
				if (isValid) {
					// dataOut["205778"]["sma"]["e88daf17"]
					dataOut[symbol][element.type][uid].push(newDatapoint);
				}
			}
		});

		return dataOut;
	}

	/**
	 * @method getCandleData
	 * precalculate diff and hollow to be used for candlestick/ohlc/hlc/posneg volume indicator rendering
	 */
	getCandleData(data) {
		const primary = this.getFirstDataset(data[this.params.symbol]['price']) || [];

		primary.forEach((row, idx) => {
			// precalculate the candle hollow flag
			row.hollow = row.close > row.open ? 1 : 0;

			// precalculate the candle diff value
			if (idx > 0) {
				// diff from previous neighbor
				row.diff = row.close - primary[idx - 1].close;
			} else {
				// calculate diff for very first point
				if (this.panels[0].isNormalized) {
					// first normalized diff is just the first close point
					row.diff = row.close;
				} else {
					// first regular diff is compared against normalizeValues.Close
					const prevClose = this.params.normalizeValues
						? this.params.normalizeValues.Close
						: null;

					if (typeof prevClose === 'number') {
						row.diff = row.close - prevClose;
					} else {
						row.diff = 0;
					}
				}
			}
		});

		return data;
	}

	/** Store off primary dataset (convenience) */
	_getPrimaryDataReference(elements, data) {
		let primary = [];
		for (const element of elements) {
			const { symbol, type, uid } = element;
			if (!primary.length && symbol === this.params.symbol) {
				primary = data[symbol][type][uid];
				break;
			}
		}

		return primary;
	}

	/** Parse the raw TimeService data into a JSON structure */
	processTimeServiceData(data = {}, args = {}) {
		const dayKeys = [
			'Sunday',
			'Monday',
			'Tuesday',
			'Wednesday',
			'Thursday',
			'Friday',
			'Saturday'
		];
		const dataTypicalIn = data.TypicalSessions || {};
		const dataTypical = [];

		let dayOfWeek;
		let minSession;
		let maxSession;
		let newSession;
		let typicalSessions;
		let rulerSession = {};
		let sessionBreak = false;

		if (!args.prepend && !args.silent) {
			if (this.state.isIntraday) {
				this.state.typicalSessions = null; // typical sessions for one single day

				// loop over each day of the week
				for (let dayIdx = 0; dayIdx < dayKeys.length; dayIdx++) {
					dataTypical[dayIdx] = {
						sessions: [], // individual sessions, used by markers
						rulerSessions: {} // one session per exchange that stretches from its min and max, used by getExchangeRuler
					};

					dayOfWeek = dayKeys[dayIdx];
					minSession = Infinity;
					maxSession = -Infinity;

					// grab all sessions for this day
					typicalSessions = dataTypicalIn[dayOfWeek] || [];

					for (const typicalSession of typicalSessions) {
						// start creating a new session object
						newSession = {
							exchangeId: typicalSession.ExchangeID,
							open: typicalSession.Open,
							close: typicalSession.Close,
							offset: typicalSession.Offset,
							sessionType:
								typicalSession.SessionType ||
								'NormalSession'
						};

						// optional premarket duration
						if (
							newSession.sessionType === 'PreMarketTrading' &&
							typeof this.params.extendedHoursDurationPre === 'number'
						) {
							newSession.open =
								newSession.close -
								this.params.extendedHoursDurationPre;
						}

						// optional postmarket duration
						if (
							newSession.sessionType === 'PostMarketTrading' &&
							typeof this.params.extendedHoursDurationPost === 'number'
						) {
							newSession.close =
								newSession.open +
								this.params.extendedHoursDurationPost;
						}

						// flag session breaks for quick lookups during renders
						if (newSession.sessionType === 'SessionBreak') {
							sessionBreak = true;
						}

						// store each individual session
						dataTypical[dayIdx].sessions.push(newSession);

						// get ruler session (one session that skips all session breaks)
						rulerSession =
							dataTypical[dayIdx].rulerSessions[
								newSession.exchangeId
							];

						if (!rulerSession) {
							dataTypical[dayIdx].rulerSessions[
								newSession.exchangeId
							] = {
								open: newSession.open,
								close: newSession.close,
								offset: newSession.offset
							};
						} else {
							rulerSession.open = Math.min(
								rulerSession.open,
								newSession.open
							);
							rulerSession.close = Math.max(
								rulerSession.close,
								newSession.close
							);
						}

						// get min/max session across all exchanges for this day
						minSession = Math.min(minSession, newSession.open);
						maxSession = Math.max(maxSession, newSession.close);
					}

					// save min/max sessions; these are used when setting the initial domain of intraday charts when domain is not specified.
					if (typicalSessions.length) {
						dataTypical[dayIdx].min = minSession;
						dataTypical[dayIdx].max = maxSession;
						dataTypical[dayIdx].duration = maxSession - minSession;
					}

					// store off a typical day containing sessions
					if (dataTypical[dayIdx].sessions.length > 0) {
						// use the day with the longest session
						if (
							!this.state.typicalSessions ||
							dataTypical[dayIdx].duration >
								this.state.typicalSessions.duration
						) {
							this.state.typicalSessions = dataTypical[dayIdx];
						}
					}
				}

				this.timeService.typicalSessions = dataTypical;
			}

			const tradingDays = data.TradingDays || [];

			this.timeService.fullClosures = this.processTimeServiceClosures(
				data.FullClosures || []
			);
			this.timeService.nonTradingDays = data.NonTradingDays || [];
			this.timeService.tradingDays = this.processTradingDays(tradingDays);
			this.timeService.status = data.Status;
			this.timeService.alternateSessions = this.processAlternateSessions(data.AlternateSessions);
			this.timeService.sessionBreak = sessionBreak;

			// warn on timeservice error
			if (this.timeService.status !== 1) {
				this.warn(`TimeService error: ${this.timeService.status}`);
			}
		}
	}

	/**
	 * @method processAlternateSessions
	 * convert ChartAPI AlternateSessions to local format
	 */
	processAlternateSessions(dates = {}) {
		const out = {};
		Object.keys(dates).forEach(date => {
			out[date] = dates[date].map(session => {
				return {
					close: session.Close,
					exchangeId: session.ExchangeID,
					offset: session.Offset,
					open: session.Open,
					sessionType: 'NormalSession'
				};
			});
		});

		return out;
	}

	/**
	 * @method processTradingDays
	 * convert various ISO formats to YYYY-MM-DDTHH:MM:SSZ
	 */
	processTradingDays(days) {
		if (!days || !days.length) {
			return [];
		}

		const isLongISO = /:/.test(days[0]) && !/Z/.test(days[0]);
		const isShortISO = /^\d{4}-\d{2}-\d{2}$/.test(days[0]);

		if (isLongISO) {
			// chartapi doesn't return the "Z"

			for (let x = 0; x < days.length; x++) {
				days[x] = `${days[x]}Z`;
			}
		} else if (isShortISO) {
			// some browsers don't like the short iso form

			for (let x = 0; x < days.length; x++) {
				days[x] = `${days[x]}T00:00:00.000Z`;
			}
		}

		return days;
	}

	/**
	 * @method processTimeServiceClosures
	 * extract closures from timeservice API response
	 */
	processTimeServiceClosures(closures) {
		const closuresOut = [];

		for (const closure of closures) {
			let date;

			if (/^\d{5}/.test(closure.Date.toString())) {
				date = this.msToJsDate(closure.Date);
			} else {
				date = new Date(closure.Date);
			}

			closuresOut.push({
				date,
				name: closure.Name,
				exchangeIds: (closure.ExchangeIDs || '').split(',')
			});
		}

		return closuresOut;
	}

	/** Set zoom enabled param and register/unregister zoom handler */
	setZoomEnabled(isEnabled) {
		if (!this.zoomUtil) return;

		this.params.zoomEnabled = typeof isEnabled === 'boolean'
			? isEnabled
			: true;

		if (this.params.zoomEnabled) {
			this.zoomUtil.registerZoom(this);
			if (this.state.updateZoomCenter) {
				this.state.updateZoomCenter = false;
				this.zoomUtil.updateZoomCenter({ chart: this });
			}
		} else {
			this.zoomUtil.unregisterZoom(this);
		}
	}

	/**
	 * Updates the chart style map. Used to apply styles (for theming) after the chart has loaded.
	 * @param {Object} newStyles - Style object containing the new styles to update
	 * @param {Boolean} ignoreCurrent - Whether or not to ignore the current styles when loading the new styles.
	 * This will cause styles not defined in newStyles to fall back to default settings.
	 * @param {Boolean} doSoftUpdate - Whether or not to keep styles that have been changed from the default
	 * (i.e. when a user changes an indicator color manually)
	 */
	updateStyles(newStyles = {}, ignoreCurrent = true, doSoftUpdate = true) {
		const oldStyles = this.params.style;
		this.params.style = ignoreCurrent
			? { ...newStyles } // Ignore the current styles
			: merge({}, this.params.style, newStyles); // Merge new styles with current styles

		// Generate the default styles, applying common style overrides
		const defaultStyles = generateDefaultStyles(this.params.styleCommon);

		// Update panel styles/sizes with new changes
		const oldStyleMap = this.styleMap;
		this.styleMap = merge({}, defaultStyles, this.params.style);
		this.panels.forEach(p => p.updateStyles(oldStyles, oldStyleMap, doSoftUpdate));

		this.updatePanelHeightPercents();

		// Resize chart and render with new params
		this.resize();
		this.render();
	}

	/** Returns the upper panel, if it exists. This should only be used for debugging. */
	get p0() {
		return this.panels[0];
	}

	/** @type {axisDate} */
	get xAxis() {
		return this.panels[0] && this.panels[0].xAxis;
	}

	/** @type {axisDate} */
	get yAxis() {
		return this.panels[0] && this.panels[0].yAxis;
	}

	/** Returns an estimate of the expected number of datapoints versus the actual number of datapoints */
	get dataDensity() {
		let expectedDataSize;
		const days = this.params.days || 365;
		const period = this.params.dataPeriod || 'Day';
		const isIntraday = this.state.isIntraday;
		const tradingDayPct = 0.7;

		if (isIntraday) { // TODO Add intraday handling
			return null;
		}

		switch (period) {
			// case 'Hour': expectedDataSize = days * dayMinutes / 60; break;
			// case 'Minute': expectedDataSize = days * dayMinutes / chart.params.dataInterval; break;
			case 'Day': expectedDataSize = days * tradingDayPct; break;
			case 'Week': expectedDataSize = (days / 7) * tradingDayPct; break;
			case 'Month': expectedDataSize = (days / 30.4) * tradingDayPct; break;
			default: expectedDataSize = 1;
		}

		return {
			dataSize: this.dataPrimary.length,
			expectedDataSize,
			description: this.dataPrimary.length / expectedDataSize < 0.9 ? 'sparse' : 'good'
		};
	}

	/**
	 * @method getNextUnusedPaletteColor
	 * Given an indicator type, find all existing colors in use. Then compare to the palette array and return the next unused color.
	 * @param {string} indicatorId - Indicator id (eg. 'price')
	 * @param {string[]} palette - Array of colors (eg. 'red' or 'rgba(255, 0, 0, 0.5)')
	 */
	getNextUnusedPaletteColor(
		indicatorId = 'price',
		palette = this.styleMap.indicator[indicatorId].palette
	) {
		const indicators = this.getIndicatorsById(indicatorId);
		if (indicators.length && palette) {
			// get current indicator colors (eg. all current price indicator colors)
			const existingColors = indicators
				.filter(i => i.params.id === indicatorId)
				.map(i => i.params.style.color);
			// find the first color from palette that is unused in existingColors
			const match = palette.filter(c => !existingColors.includes(c));
			return match.length ? match[0] : existingColors[0];
		}
	}

	/**
	 * Utility function to calculate the total trading minutes for a given array of sessions.
	 * This function handles overlapping sessions to ensure accurate calculation of trading time.
	 * @param {Array} sessions - An array of session objects, each containing 'open' and 'close' times in minutes.
	 * @returns {number} - The total trading minutes.
	 */
	calculateTotalTradingMinutes(sessions) {
		let totalMinutes = 0;
		let lastClose = 0;

		for (const session of sessions) {
			if (session.open > lastClose) {
				totalMinutes += session.close - session.open;
			} else {
				totalMinutes += Math.max(0, session.close - lastClose);
			}
			lastClose = session.close;
		}

		return totalMinutes;
	}

	/**
	 * Returns a dynamic period/interval based on the number of days, chart width, and desired spacing between data points.
	 * This method calculates the appropriate data interval and period to use for charting purposes.
	 * @param {number} days - The number of days to display on the chart.
	 * @param {number} chartWidth - The width of the chart in pixels (default is 500).
	 * @param {number} desiredSpacing - The desired space between data points in pixels (default is 3).
	 * @returns {Object} - An object containing the calculated data interval and data period.
	 */
	getBestFitPeriodInterval(days = this.params.days, chartWidth = 500, desiredSpacing = 3) {
		// Update chartWidth if a panel's width is available
		if (this.panels.length && this.panels[0].size?.width) chartWidth = this.panels[0].size.width;

		let totalMinutesPerDay = 390; // Default to 390 minutes / 6.5 hours

		// Check if Monday's typical sessions are available and use them to calculate total trading minutes
		if (this.timeService?.typicalSessions && this.timeService.typicalSessions.length > 1) {
			const mondaySessions = this.timeService.typicalSessions[1].sessions;
			if (mondaySessions && mondaySessions.length > 0) {
				totalMinutesPerDay = this.calculateTotalTradingMinutes(mondaySessions);
			}
		}

		// Base data points per day for different periods
		const baseDatapointsPerDay = {
			Minute: totalMinutesPerDay,
			Hour: totalMinutesPerDay / 60,
			Day: 1,
			Week: 1 / 7,
			Month: 1 / 30
		};

		let dataInterval;
		let dataPeriod;
		let minDiff = Infinity;

		// Determine intervals and periods based on the number of days
		const intervals = days < 22 ? [1, 3, 5, 15] : [1];
		const periods = days < 22 ? ['Minute', 'Hour'] : ['Day', 'Week', 'Month'];

		// Calculate the best data interval and period based on desired spacing
		for (const period of periods) {
			for (const interval of intervals) {
				const totalDatapoints = baseDatapointsPerDay[period] * days / interval;
				const spacing = chartWidth / totalDatapoints;
				const diff = Math.abs(desiredSpacing - spacing);

				if (diff < minDiff) {
					minDiff = diff;
					dataInterval = interval;
					dataPeriod = period;
				}
			}
		}

		return { dataInterval, dataPeriod };
	}

	/** Determines the last exchangeDates index given the available data */
	_calcLastIndex() {
		let lastIndex;

		if (this.params.zoomResetBoundary === 'ruler') {
			// The right edge of the present domain seems to look better than the exchangeDates length
			lastIndex = this.state.xAxisPresentScale.domain()[1];
		} else {
			if (this.state.isIntraday && this.state.typicalSessions) {
				// boundary is edge of most recent intraday session
				const typicalClose = new Date(
					this.dataPrimary[this.dataPrimary.length - 1].date
				);
				typicalClose.setHours(0);
				typicalClose.setSeconds(0);
				typicalClose.setMilliseconds(0);
				typicalClose.setMinutes(this.state.typicalSessions.max);

				lastIndex = Math.min(
					this.exchangeDates.length - 1,
					this.closestExchangeIndex(typicalClose) - 1
				);
			} else {
				// boundary is edge of data
				lastIndex = this.dataPrimary[this.dataPrimary.length - 1].dateIndex;
			}
		}

		return lastIndex;
	}
}
